import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventsApi, tournamentApi, searchApi, teamAdminApi, adminApi, assetUrl } from '../utils/api';
import EventAnalytics from './EventAnalytics';
import ReportsTab from './ReportsTab';
import {
  IconGrid, IconBarChart, IconUsers, IconUser, IconStar,
  IconScale, IconMonitor, IconTrophy, IconTarget, IconBox,
  IconFish, IconEdit, IconWarning, IconInfo, IconFile,
} from '../components/Icons';
import { eventStatusLabel } from '../utils/eventStatus';

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isOrganizer } = useAuth();
  const [event, setEvent] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview');
  const [highlightUserId, setHighlightUserId] = useState(null);

  useEffect(() => { fetchAll(); }, [eventId]);

  async function fetchAll() {
    try {
      const [eventRes, tournRes] = await Promise.all([
        eventsApi.getEvent(eventId),
        tournamentApi.getTournaments(eventId)
      ]);
      setEvent(eventRes.data.data);
      setTournaments(tournRes.data.data);

      // Only fetch registrations/teams if organizer or admin
      if (isOrganizer || isAdmin) {
        const [regRes, teamRes] = await Promise.all([
          eventsApi.getRegistrations(eventId),
          tournamentApi.getTeams(eventId)
        ]);
        setRegistrations(regRes.data.data);
        setTeams(teamRes.data.data);
      }
    } catch (err) {
      console.error('Event detail error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="spinner" />;
  if (!event) return <div style={{ padding: 40, textAlign: 'center', color: '#8a9bb5' }}>Event not found</div>;

  const canManage = isAdmin || (isOrganizer && event.organizer_id === user.id);
  const tabs = canManage
    ? ['overview', 'tournaments', 'teams', 'participants', 'sponsors', 'weigh-in', 'scoreboard', 'reports', 'analytics']
    : ['overview'];

  return (
    <div className="page">
      <div className="page-content">
        {/* Header */}
        <div className="event-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0 }}>{event.name}</h1>
              <span className={`badge badge-${event.payment_status}`}>{event.payment_status}</span>
            </div>
            <p style={{ color: '#8a9bb5', marginTop: 4 }}>
              {event.location || 'No location'} · {new Date(event.start_date).toLocaleDateString()} – {new Date(event.end_date).toLocaleDateString()}
            </p>
          </div>
          <div className="event-header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Link to={`/events/${eventId}/scoreboard`} className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
              <IconMonitor size="14px" style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} /> Scoreboard
            </Link>
            {canManage && <EventStatusToggle status={event.status} onStatusChange={updateStatus} />}
          </div>
        </div>

        {/* Tabs */}
        <div className="event-tabs" style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2a3f56', paddingBottom: 0 }}>
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              background: 'none', border: 'none', color: activeTab === tab ? '#e8a838' : '#8a9bb5',
              padding: '10px 16px', cursor: 'pointer', fontFamily: 'IBM Plex Sans, sans-serif',
              fontSize: '0.85rem', fontWeight: activeTab === tab ? 600 : 400,
              borderBottom: activeTab === tab ? '2px solid #e8a838' : '2px solid transparent',
              textTransform: 'capitalize', transition: 'all 0.2s'
            }}>
              {(() => {
                const TAB_ICONS = {
                  overview:      <IconGrid size="13px" />,
                  tournaments:   <IconBarChart size="13px" />,
                  teams:         <IconUsers size="13px" />,
                  participants:  <IconUser size="13px" />,
                  sponsors:      <IconStar size="13px" />,
                  'weigh-in':    <IconScale size="13px" />,
                  scoreboard:    <IconMonitor size="13px" />,
                  reports:       <IconFile size="13px" />,
                  analytics:     <IconBarChart size="13px" />,
                };
                const TAB_LABELS = {
                  'weigh-in':   'Catch Logs',
                  participants: 'Participants',
                };
                const icon = TAB_ICONS[tab];
                const label = TAB_LABELS[tab] || (tab.charAt(0).toUpperCase() + tab.slice(1));
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {icon}{label}
                  </span>
                );
              })()}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && <OverviewTab event={event} tournaments={tournaments} registrations={registrations} teams={teams} onRefresh={fetchAll} />}
        {activeTab === 'tournaments' && <TournamentsTab eventId={eventId} event={event} tournaments={tournaments} onRefresh={fetchAll} />}
        {activeTab === 'weigh-in' && <WeighInTab eventId={eventId} event={event} tournaments={tournaments} registrations={registrations} teams={teams} onRefresh={fetchAll} />}
        {activeTab === 'participants' && <RegistrationsTab registrations={registrations} eventId={eventId} event={event} teams={teams} tournaments={tournaments} onRefresh={fetchAll} highlightUserId={highlightUserId} clearHighlight={() => setHighlightUserId(null)} />}
        {activeTab === 'teams' && <TeamsTab eventId={eventId} event={event} teams={teams} tournaments={tournaments} registrations={registrations} onRefresh={fetchAll} setActiveTab={setActiveTab} setHighlightUserId={setHighlightUserId} />}
        {activeTab === 'sponsors' && <SponsorsTab eventId={eventId} event={event} onRefresh={fetchAll} />}
        {activeTab === 'scoreboard' && <ScoreboardTab eventId={eventId} event={event} tournaments={tournaments} onRefresh={fetchAll} />}
        {activeTab === 'reports' && <ReportsTab eventId={eventId} event={event} />}
        {activeTab === 'analytics' && <EventAnalytics />}
      </div>
    </div>
  );

  async function updateStatus(status) {
    try {
      await eventsApi.updateStatus(eventId, { status });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  }
}

// ─── EVENT STATUS TOGGLE ─────────────────────────────────────
const STATUS_STATES = [
  { value: 'active',    label: eventStatusLabel('active'),    color: '#8a9bb5', bg: 'rgba(138,155,181,0.15)', icon: '○' },
  { value: 'live',      label: eventStatusLabel('live'),      color: '#4a9eff', bg: 'rgba(74,158,255,0.15)',  icon: '●' },
  { value: 'completed', label: eventStatusLabel('completed'), color: '#3adc8a', bg: 'rgba(58,220,138,0.15)', icon: '✓' },
];

function EventStatusToggle({ status, onStatusChange }) {
  const currentIdx = STATUS_STATES.findIndex(s => s.value === status);
  const current = STATUS_STATES[currentIdx] || STATUS_STATES[0];
  const nextIdx = currentIdx >= 0 ? (currentIdx + 1) % STATUS_STATES.length : 0;
  const next = STATUS_STATES[nextIdx];

  function handleClick() {
    const msg = `Change event status to "${next.label}"?`;
    if (window.confirm(msg)) {
      onStatusChange(next.value);
    }
  }

  return (
    <button
      onClick={handleClick}
      title={`Click to change to: ${next.label}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', borderRadius: 8,
        background: current.bg, border: `1px solid ${current.color}`,
        color: current.color, cursor: 'pointer',
        fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.82rem', fontWeight: 600,
        transition: 'all 0.2s', whiteSpace: 'nowrap'
      }}
    >
      <span style={{ fontSize: '0.9rem' }}>{current.icon}</span>
      {current.label}
    </button>
  );
}

// ─── OVERVIEW TAB ────────────────────────────────────────────
// ─── EVENT ORGANIZER (master_admin only) ─────────────────────
function OrganizerCard({ event, onRefresh }) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const [organizers, setOrganizers] = useState([]);
  const [selectedId, setSelectedId] = useState(event.organizer_id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isAdmin) return null;

  async function startEdit() {
    setEditing(true);
    setError('');
    setSelectedId(event.organizer_id);
    setLoading(true);
    try {
      const res = await adminApi.getOrganizers({ limit: 500 });
      setOrganizers(res.data.data || []);
    } catch (err) {
      setError('Failed to load organizers');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!selectedId || selectedId === event.organizer_id) return;
    const target = organizers.find(o => o.id === selectedId);
    const targetName = target ? `${target.first_name} ${target.last_name}` : 'the selected organizer';
    if (!window.confirm(`Transfer "${event.name}" to ${targetName}? The current organizer will lose access to this event.`)) return;
    setSaving(true);
    setError('');
    try {
      await adminApi.updateEventOrganizer(event.id, { organizer_id: selectedId });
      setEditing(false);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change organizer');
    } finally {
      setSaving(false);
    }
  }

  // Hide deactivated accounts, but never drop the current owner from the list
  const options = organizers.filter(o => o.is_active !== false || o.id === event.organizer_id);
  const currentInList = options.some(o => o.id === event.organizer_id);

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Event Organizer</div>
        {!editing && (
          <button className="btn btn-ghost btn-sm" style={{ color: '#4a9eff' }} onClick={startEdit}>Change</button>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!editing ? (
        <div>
          <div style={{ fontSize: '1.05rem', fontFamily: 'Oswald, sans-serif', fontWeight: 600, color: '#f0f2f5' }}>
            {event.organizer_name || '—'}
          </div>
          {event.organizer_email && (
            <div style={{ color: '#5c6d83', fontSize: '0.8rem', marginTop: 2 }}>{event.organizer_email}</div>
          )}
        </div>
      ) : loading ? (
        <div style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>Loading organizers…</div>
      ) : (
        <>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Assign to</label>
            <select value={selectedId || ''} onChange={e => setSelectedId(e.target.value)} disabled={saving}>
              {!currentInList && (
                <option value={event.organizer_id}>{event.organizer_name} (current)</option>
              )}
              {options.map(o => (
                <option key={o.id} value={o.id}>
                  {o.first_name} {o.last_name} — {o.email}{o.id === event.organizer_id ? ' (current)' : ''}
                </option>
              ))}
            </select>
            <small style={{ color: '#8a9bb5', display: 'block', marginTop: 6, fontSize: '0.8rem' }}>
              The new organizer gets full management access to this event. The previous organizer loses it. Past invoices are unchanged.
            </small>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !selectedId || selectedId === event.organizer_id}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setError(''); }} disabled={saving}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

function OverviewTab({ event, tournaments, registrations, teams, onRefresh }) {
  const isTeamEvent = event?.event_type !== 'individual';
  const tier = event.tier;
  // Count only parent tournaments (not multi-day child variants)
  const parentTournaments = tournaments.filter(t => !t.parent_tournament_id);
  const [uploading, setUploading] = useState(false);

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!allowed.includes(file.type)) { alert('Image must be JPG, PNG, WebP, or AVIF'); return; }
    if (file.size > 4 * 1024 * 1024) { alert('Image must be under 4MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('featured_image', file);
      await eventsApi.uploadFeaturedImage(event.id, fd);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  }

  async function handleImageRemove() {
    if (!window.confirm('Remove featured image?')) return;
    try {
      await eventsApi.deleteFeaturedImage(event.id);
      onRefresh();
    } catch (err) {
      alert('Failed to remove image');
    }
  }

  return (
    <>
      <OrganizerCard event={event} onRefresh={onRefresh} />

      {/* Featured Image */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Featured Image</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ color: '#4a9eff', fontSize: '0.8rem', cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.5 : 1 }}>
              {uploading ? 'Uploading...' : event.featured_image_url ? 'Replace' : 'Upload'}
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={handleImageUpload} disabled={uploading} style={{ display: 'none' }} />
            </label>
            {event.featured_image_url && (
              <button onClick={handleImageRemove} style={{ background: 'none', border: 'none', color: '#e85d5d', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>Remove</button>
            )}
          </div>
        </div>
        {event.featured_image_url ? (
          <img src={assetUrl(event.featured_image_url)} alt={event.name} style={{ width: '100%', maxHeight: 900, objectFit: 'contain', borderRadius: 10 }} />
        ) : (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#5c6d83', fontSize: '0.85rem', border: '2px dashed #2a3f56', borderRadius: 10 }}>
            No featured image — click Upload to add one
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <StatBox
          label={isTeamEvent ? 'Teams' : 'Participants'}
          value={isTeamEvent ? teams.length : registrations.length}
          sub={isTeamEvent && registrations.length > 0 ? `${registrations.length} participant${registrations.length !== 1 ? 's' : ''} total` : undefined}
        />
        <StatBox label="Tournaments" value={parentTournaments.length} sub={tournaments.length > parentTournaments.length ? `${tournaments.length - parentTournaments.length} day variant${tournaments.length - parentTournaments.length !== 1 ? 's' : ''}` : undefined} />
        <StatBox label="Tier" value={tier?.tier_name || event.pricing_tier_name || 'N/A'} sub={tier ? `Up to ${tier.max_attendees}` : ''} />
        <StatBox label="Amount Paid" value={`$${parseFloat(event.amount_paid || 0).toFixed(2)}`} sub="CAD" color="#e8a838" />
      </div>
    </>
  );
}

function StatBox({ label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontFamily: 'Oswald, sans-serif', fontWeight: 600, color: color || '#f0f2f5' }}>{value}</div>
      {sub && <div style={{ color: '#5c6d83', fontSize: '0.75rem', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── TOURNAMENTS TAB ─────────────────────────────────────────
function TournamentForm({ form, setForm, onSubmit, onCancel, submitLabel, error, radioPrefix, tournaments = [], editingId = null }) {
  const isBoxType = form.tournament_type === 'box_weight';
  const isCalcutta = form.tournament_type === 'calcutta';
  const isPoints = form.scoring_mode === 'points';
  const isSecondary = !form.is_main_tournament;
  const pfx = radioPrefix || 'create';

  // Tournament category locking logic
  const activeTournaments = tournaments.filter(t => t.is_active !== false && !t.parent_tournament_id);
  const existingMain = activeTournaments.find(t => t.is_main_tournament && t.id !== editingId);
  const isFirstTournament = activeTournaments.filter(t => t.id !== editingId).length === 0;
  const categoryLocked = isFirstTournament || !!existingMain || (editingId && form.is_main_tournament);

  let categoryHelperText = '';
  if (isFirstTournament) {
    categoryHelperText = 'The first tournament you create must be the main tournament. You can add secondary tournaments afterwards.';
  } else if (existingMain && !editingId) {
    categoryHelperText = `"${existingMain.name}" is already the main tournament for this event. Additional tournaments must be secondary.`;
  } else if (editingId && form.is_main_tournament) {
    categoryHelperText = 'The main tournament cannot be changed to secondary.';
  }

  return (
    <form onSubmit={onSubmit}>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* ── CATEGORY ── */}
      <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Tournament Category</label>
          <div className="radio-group" style={{ display: 'flex', gap: 12 }}>
            {[{ val: true, label: 'Main Tournament' }, { val: false, label: 'Secondary Tournament' }].map(opt => {
              const disabled = categoryLocked && form.is_main_tournament !== opt.val;
              return (
                <label key={String(opt.val)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', padding: '10px 16px', borderRadius: 8, border: `2px solid ${form.is_main_tournament === opt.val ? '#e8a838' : '#2a3f56'}`, background: form.is_main_tournament === opt.val ? 'rgba(232,168,56,0.08)' : 'transparent', flex: 1, justifyContent: 'center', opacity: disabled ? 0.5 : 1 }}>
                  <input type="radio" name={`${pfx}_category`} value={String(opt.val)} checked={form.is_main_tournament === opt.val} onChange={() => !disabled && setForm({ ...form, is_main_tournament: opt.val })} disabled={disabled} style={{ display: 'none' }} />
                  <span style={{ color: form.is_main_tournament === opt.val ? '#e8a838' : '#8a9bb5', fontWeight: 600, fontSize: '0.88rem' }}>{opt.label}</span>
                </label>
              );
            })}
          </div>
          {categoryHelperText && (
            <small style={{ color: '#8a9bb5', display: 'block', marginTop: 6, fontSize: '0.8rem' }}>{categoryHelperText}</small>
          )}
        </div>

        {/* ── NAME & DAYS ── */}
        <div className="form-group">
          <label>Tournament Name</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Salmon Slam Classic" required />
        </div>

        <div className="form-group">
          <label>Number of Days</label>
          <input type="number" min="1" max="10" value={form.num_days} onChange={e => setForm({ ...form, num_days: parseInt(e.target.value) || 1 })} />
          {form.num_days > 1 && (
            <small style={{ color: '#8a9bb5', display: 'block', marginTop: 4 }}>
              Will create: {Array.from({ length: form.num_days }, (_, i) => `"${form.name || 'Tournament'} (Day ${i + 1})"`).join(', ')}
            </small>
          )}
        </div>

        {/* ── TOURNAMENT TYPE ── */}
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Tournament Type</label>
          <div className="radio-group" style={{ display: 'flex', gap: 12 }}>
            {[
              { val: 'box_weight', label: 'Total Box Weight', icon: <IconBox size="18px" /> },
              { val: 'heaviest_single', label: 'Heaviest Single Fish', icon: <IconFish size="18px" /> },
              { val: 'custom', label: 'Custom Rules', icon: <IconTarget size="18px" /> },
              ...(isSecondary ? [{ val: 'calcutta', label: 'Based on Main Event (Calcutta)', icon: <IconTarget size="18px" /> }] : [])
            ].map(opt => (
              <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 16px', borderRadius: 8, border: `2px solid ${form.tournament_type === opt.val ? '#e8a838' : '#2a3f56'}`, background: form.tournament_type === opt.val ? 'rgba(232,168,56,0.08)' : 'transparent', flex: 1, justifyContent: 'center' }}>
                <input type="radio" name={`${pfx}_type`} value={opt.val} checked={form.tournament_type === opt.val} onChange={() => setForm({ ...form, tournament_type: opt.val })} style={{ display: 'none' }} />
                <span style={{ fontSize: '1rem' }}>{opt.icon}</span>
                <span style={{ color: form.tournament_type === opt.val ? '#e8a838' : '#8a9bb5', fontWeight: 600, fontSize: '0.82rem' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* ── BOX ENTRY SETTINGS (box_weight only) ── */}
      {isBoxType && (
        <div style={{ background: '#111f2e', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
          <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Box Entry Settings</div>
          <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Max Fish Per Box</label>
              <input type="number" min="1" max="20" value={form.fish_per_box} onChange={e => setForm({ ...form, fish_per_box: parseInt(e.target.value) || 6 })} placeholder="e.g. 6" />
              <small style={{ color: '#5c6d83', display: 'block', marginTop: 3 }}>Teams over this limit are disqualified</small>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.has_big_fish_contest} onChange={e => setForm({ ...form, has_big_fish_contest: e.target.checked })} />
                <div>
                  <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.88rem' }}>Include Big Fish Contest</div>
                  <div style={{ color: '#5c6d83', fontSize: '0.75rem' }}>Biggest fish field added to catch log</div>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ── CALCUTTA SETTINGS ── */}
      {isCalcutta && (
        <div style={{ background: '#111f2e', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
          <div style={{ color: '#9b59b6', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Calcutta Settings</div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Source Tournament</label>
            <select value={form.source_tournament_id || ''} onChange={e => setForm({ ...form, source_tournament_id: e.target.value || null })}>
              <option value="">— Auto (first main tournament) —</option>
              {tournaments.filter(t => t.tournament_type !== 'calcutta' && !t.parent_tournament_id && t.id !== editingId).map(t => (
                <option key={t.id} value={t.id}>{t.name}{t.is_main_tournament ? ' (Main)' : ''}</option>
              ))}
            </select>
          </div>
          <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Track Metric</label>
              <select value={form.calcutta_tracks} onChange={e => setForm({ ...form, calcutta_tracks: e.target.value })}>
                <option value="overall">Overall Winner (Final Standings)</option>
                <option value="heaviest_fish">Heaviest Single Fish (All Days)</option>
                <option value="heaviest_fish_daily">Heaviest Single Fish (Per Day)</option>
                <option value="heaviest_box">Heaviest Box (Any Single Day)</option>
                <option value="day_winner">Specific Day Winner</option>
              </select>
            </div>
            {form.calcutta_tracks === 'day_winner' && (
              <div className="form-group">
                <label>Which Day</label>
                <input type="number" min="1" max="10" value={form.calcutta_day} onChange={e => setForm({ ...form, calcutta_day: parseInt(e.target.value) || 1 })} />
              </div>
            )}
          </div>
          <div style={{ background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: 6, padding: '10px 14px', marginTop: 8, fontSize: '0.78rem', color: '#c39bd3' }}>
            <IconInfo size="13px" style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} />Calcutta rankings are auto-calculated from the selected source tournament. No catch logs needed.
          </div>
        </div>
      )}

      {/* ── CUSTOM TOURNAMENT SETTINGS ── */}
      {form.tournament_type === 'custom' && (
        <div style={{ background: '#111f2e', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
          <div style={{ color: '#4a9eff', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Custom Tournament Rules</div>
          <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={form.start_date || ''} onChange={e => setForm({ ...form, start_date: e.target.value || null })} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" value={form.end_date || ''} onChange={e => setForm({ ...form, end_date: e.target.value || null })} />
            </div>
            <div className="form-group">
              <label>Max Total Fish (per team, all days)</label>
              <input type="number" min="1" value={form.max_total_fish || ''} onChange={e => setForm({ ...form, max_total_fish: parseInt(e.target.value) || null })} placeholder="No limit" />
            </div>
            <div className="form-group">
              <label>Min Weight (lbs)</label>
              <input type="number" step="0.01" min="0" value={form.min_weight_lbs || ''} onChange={e => setForm({ ...form, min_weight_lbs: parseFloat(e.target.value) || null })} placeholder="No minimum" />
            </div>
            <div className="form-group">
              <label>Max Entries Per Team Per Day</label>
              <input type="number" min="1" value={form.max_entries_per_team_per_day || ''} onChange={e => setForm({ ...form, max_entries_per_team_per_day: parseInt(e.target.value) || null })} placeholder="No limit" />
            </div>
            <div className="form-group">
              <label>Scoreboard Layout</label>
              <select value={form.scoreboard_layout || 'leaderboard'} onChange={e => setForm({ ...form, scoreboard_layout: e.target.value })}>
                <option value="leaderboard">Ranked Leaderboard</option>
                <option value="daily_winners">Daily Winners</option>
              </select>
            </div>
          </div>
          {form.scoreboard_layout === 'daily_winners' && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Rollover Display</label>
              <select value={form.rollover_display_mode || 'pill'} onChange={e => setForm({ ...form, rollover_display_mode: e.target.value })}>
                <option value="pill">Rollover Pill (badge on day row)</option>
                <option value="carry_forward">Carry Forward Indicator</option>
              </select>
            </div>
          )}
          <div style={{ background: 'rgba(74,158,255,0.1)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: '0.78rem', color: '#7ec8e3' }}>
            Custom tournaments support rule violations at weigh-in (warn + override). Species group quotas can be configured after creation.
          </div>
        </div>
      )}

      {/* ── SCORING MODE ── */}
      {!isCalcutta && (
        <div style={{ background: '#111f2e', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
          <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Scoring</div>

          <div className="form-group">
            <label>Scoring Mode</label>
            <div className="radio-group" style={{ display: 'flex', gap: 10 }}>
              {[
                { val: 'points', label: 'Points (Auto)' },
                { val: 'manual', label: 'Manual Score' },
                { val: 'none', label: 'Weight Only' }
              ].map(opt => (
                <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 14px', borderRadius: 8, border: `2px solid ${form.scoring_mode === opt.val ? '#e8a838' : '#2a3f56'}`, background: form.scoring_mode === opt.val ? 'rgba(232,168,56,0.08)' : 'transparent', flex: 1, justifyContent: 'center' }}>
                  <input type="radio" name={`${pfx}_scoring_mode`} value={opt.val} checked={form.scoring_mode === opt.val} onChange={() => setForm({ ...form, scoring_mode: opt.val })} style={{ display: 'none' }} />
                  <span style={{ color: form.scoring_mode === opt.val ? '#e8a838' : '#8a9bb5', fontWeight: 600, fontSize: '0.8rem' }}>{opt.label}</span>
                </label>
              ))}
            </div>
            {form.scoring_mode === 'manual' && <small style={{ color: '#5c6d83', display: 'block', marginTop: 6 }}>Organizer enters score manually at weigh-in. No auto-calculation.</small>}
            {form.scoring_mode === 'none' && <small style={{ color: '#5c6d83', display: 'block', marginTop: 6 }}>Weight recorded only. No score shown on scoreboard.</small>}
          </div>

          {/* Points sub-options */}
          {isPoints && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #1e2d3d' }}>
              <div className="grid-responsive-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div className="form-group">
                  <label>Points per lb</label>
                  <input type="number" step="0.01" min="0" value={form.points_per_pound} onChange={e => setForm({ ...form, points_per_pound: parseFloat(e.target.value) || 0 })} placeholder="0 = disabled" />
                </div>
                <div className="form-group">
                  <label>Points per fish</label>
                  <input type="number" step="0.01" min="0" value={form.points_per_fish} onChange={e => setForm({ ...form, points_per_fish: parseFloat(e.target.value) || 0 })} placeholder="0 = disabled" />
                </div>
                <div className="form-group">
                  <label>Bonus for biggest fish</label>
                  <input type="number" step="0.01" min="0" value={form.points_for_big_fish} onChange={e => setForm({ ...form, points_for_big_fish: parseFloat(e.target.value) || 0 })} placeholder="0 = disabled" />
                </div>
              </div>
              {/* AND/OR combination — only show if more than one points type is active */}
              {[form.points_per_pound, form.points_per_fish, form.points_for_big_fish].filter(v => v > 0).length > 1 && (
                <div className="form-group">
                  <label>Combine methods</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[{ val: 'and', label: 'AND (add together)' }, { val: 'or', label: 'OR (use highest)' }].map(opt => (
                      <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 14px', borderRadius: 8, border: `2px solid ${form.scoring_combination === opt.val ? '#4a9eff' : '#2a3f56'}`, background: form.scoring_combination === opt.val ? 'rgba(74,158,255,0.08)' : 'transparent' }}>
                        <input type="radio" name={`${pfx}_scoring_combination`} value={opt.val} checked={form.scoring_combination === opt.val} onChange={() => setForm({ ...form, scoring_combination: opt.val })} style={{ display: 'none' }} />
                        <span style={{ color: form.scoring_combination === opt.val ? '#4a9eff' : '#8a9bb5', fontWeight: 600, fontSize: '0.8rem' }}>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  {form.scoring_combination === 'and' && form.points_per_pound > 0 && form.points_per_fish > 0 && (
                    <small style={{ color: '#5c6d83', display: 'block', marginTop: 4 }}>
                      e.g. 5 fish @ 70 lbs → {(70 * form.points_per_pound) + (5 * form.points_per_fish)} pts ({form.points_per_pound} pts/lb + {form.points_per_fish} pts/fish)
                    </small>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── FISH SPECIES TRACKING ── */}
      {!isCalcutta && (
        <div style={{ background: '#111f2e', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
          <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Fish Species</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: form.track_species ? 16 : 0 }}>
            <input type="checkbox" checked={!!form.track_species} onChange={e => setForm({ ...form, track_species: e.target.checked })} />
            <div>
              <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.88rem' }}>Track Fish Species in Catch Logs</div>
              <div style={{ color: '#5c6d83', fontSize: '0.75rem' }}>Add a species dropdown to catch log entries for this tournament</div>
            </div>
          </label>
          {form.track_species && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  value={form._newSpecies || ''}
                  onChange={e => setForm({ ...form, _newSpecies: e.target.value })}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = (form._newSpecies || '').trim();
                      if (val && !(form.species_list || []).includes(val)) {
                        setForm({ ...form, species_list: [...(form.species_list || []), val], _newSpecies: '' });
                      }
                    }
                  }}
                  placeholder="e.g. Walleye, Pike, Bass..."
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                  const val = (form._newSpecies || '').trim();
                  if (val && !(form.species_list || []).includes(val)) {
                    setForm({ ...form, species_list: [...(form.species_list || []), val], _newSpecies: '' });
                  }
                }}>Add</button>
              </div>
              {(form.species_list || []).length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(form.species_list || []).map((sp, i) => (
                    <span key={i} style={{ background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', color: '#f0f2f5', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {sp}
                      <button type="button" onClick={() => setForm({ ...form, species_list: (form.species_list || []).filter((_, j) => j !== i) })}
                        style={{ background: 'none', border: 'none', color: '#e85d5d', cursor: 'pointer', padding: 0, fontSize: '0.9rem', lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              ) : (
                <small style={{ color: '#5c6d83' }}>No species added yet. Type a species name and press Enter or click Add.</small>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── SECONDARY OPTIONS ── */}
      {isSecondary && !isCalcutta && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.add_to_main_overall} onChange={e => setForm({ ...form, add_to_main_overall: e.target.checked })} />
            <div>
              <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.88rem' }}>Add score to Main Tournament Overall</div>
              <div style={{ color: '#5c6d83', fontSize: '0.75rem' }}>e.g. a pre-tournament shootout bonus that carries over</div>
            </div>
          </label>
        </div>
      )}

      {/* ── LIVE SCOREBOARD OUTPUTS ── */}
      {!isCalcutta && (() => {
        const isDailyWinners = form.tournament_type === 'custom' && form.scoreboard_layout === 'daily_winners';
        const cols = form.scoreboard_columns || {};
        // Derive effective columns based on constraints
        const effective = { ...cols };
        if (isDailyWinners) { effective.day = true; effective.fish_count = false; }
        if ((form.num_days || 1) <= 1 && !isDailyWinners) effective.day = false;
        if (!form.has_big_fish_contest) effective.big_fish = false;
        if (!form.track_species && form.tournament_type !== 'custom') effective.species = false;
        if (form.scoring_mode === 'none') effective.points = false;

        const toggles = [
          { key: 'team_name', label: 'Team/Participant Name' },
          { key: 'team_number', label: 'Team/Participant Number' },
          { key: 'fish_count', label: 'Fish Count', disabled: isDailyWinners, hint: isDailyWinners ? 'Hidden in Daily Winners layout' : null },
          { key: 'day', label: 'Day', disabled: (form.num_days || 1) <= 1 && !isDailyWinners, hint: isDailyWinners ? 'Required for Daily Winners layout' : (form.num_days || 1) <= 1 ? 'Multi-day tournaments only' : null, note: form.tournament_type === 'custom' && form.scoring_mode === 'none' ? 'When unchecked, scoreboard shows individual catches as columns per team instead of grouped totals' : null },
          { key: 'species', label: 'Species', disabled: !form.track_species && form.tournament_type !== 'custom', hint: !form.track_species && form.tournament_type !== 'custom' ? 'Enable Track Fish Species above first' : null },
          { key: 'weight', label: 'Weight' },
          { key: 'points', label: 'Points', disabled: form.scoring_mode === 'none', hint: form.scoring_mode === 'none' ? 'Tournament uses weight-only ranking' : null },
          { key: 'spread', label: 'Spread' },
          { key: 'big_fish', label: 'Big Fish', disabled: !form.has_big_fish_contest, hint: !form.has_big_fish_contest ? 'Enable Big Fish Contest above first' : null },
        ];

        return (
          <div style={{ background: '#111f2e', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Live Scoreboard Outputs</div>
            <div style={{ color: '#5c6d83', fontSize: '0.78rem', marginBottom: 12 }}>
              Choose which columns appear on the live scoreboard for this tournament.
            </div>
            {isDailyWinners && (
              <div style={{ background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: '0.75rem', color: '#7bb8f5' }}>
                Daily Winners layout requires the Day column and hides Fish Count.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {toggles.map(({ key, label, disabled, hint, note }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, padding: '8px 12px', borderRadius: 6, background: '#0d1b2a' }}>
                  <input
                    type="checkbox"
                    checked={!!effective[key]}
                    disabled={disabled}
                    onChange={e => {
                      const updated = { ...cols, [key]: e.target.checked };
                      setForm({ ...form, scoreboard_columns: updated });
                    }}
                  />
                  <div>
                    <div style={{ color: disabled ? '#5c6d83' : '#f0f2f5', fontSize: '0.82rem', fontWeight: 500 }}>{label}</div>
                    {hint && <div style={{ color: '#5c6d83', fontSize: '0.7rem', marginTop: 1 }}>{hint}</div>}
                    {note && !disabled && <div style={{ color: '#7bb8f5', fontSize: '0.68rem', marginTop: 2 }}>{note}</div>}
                  </div>
                </label>
              ))}
            </div>
            {/* Weight decimal places */}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 6, background: '#0d1b2a' }}>
              <label style={{ color: '#f0f2f5', fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Weight Output (Decimal Places)</label>
              <input
                type="number"
                min={0}
                max={4}
                value={form.weight_decimals ?? 2}
                onChange={e => setForm({ ...form, weight_decimals: Math.min(Math.max(parseInt(e.target.value) || 0, 0), 4) })}
                style={{ width: 60, padding: '6px 10px', borderRadius: 6, border: '1px solid #2a3a4e', background: '#0a1628', color: '#f0f2f5', fontSize: '0.85rem', textAlign: 'center' }}
              />
              <span style={{ color: '#5c6d83', fontSize: '0.72rem' }}>0–4 decimal places for weight on scoreboard &amp; reports</span>
            </div>
          </div>
        );
      })()}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="submit" className="btn btn-primary">
          {submitLabel}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const typeLabel = t => {
  if (t.tournament_type === 'box_weight') return 'Box Weight';
  if (t.tournament_type === 'heaviest_single') return 'Heaviest Single Fish';
  if (t.tournament_type === 'calcutta') return 'Calcutta';
  if (t.tournament_type === 'custom') return 'Custom';
  return t.tournament_type;
};

const scoringLabel = t => {
  if (t.scoring_mode === 'manual') return 'Manual Score';
  if (t.scoring_mode === 'none') return 'Weight Only';
  const parts = [];
  if (t.points_per_pound > 0) parts.push(`${t.points_per_pound} pts/lb`);
  if (t.points_per_fish > 0) parts.push(`${t.points_per_fish} pts/fish`);
  if (t.points_for_big_fish > 0) parts.push(`${t.points_for_big_fish} pts big fish`);
  return parts.length ? parts.join(` ${t.scoring_combination?.toUpperCase() || 'AND'} `) : 'Points';
};

function TournamentCard({ t, tournaments, editingId, editForm, setEditForm, handleEdit, handleDelete, startEdit, setEditingId, setEditError, editError }) {
  const dayVariants = tournaments.filter(d => d.parent_tournament_id === t.id);
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: '#f0f2f5' }}>{t.name.replace(/\s*\(Day\s*\d+\)\s*$/, '')}</span>
              {t.tournament_type === 'calcutta' && (
                <span style={{ background: '#9b59b6', color: '#fff', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>CALCUTTA</span>
              )}
              {t.has_big_fish_contest && (
                <span style={{ background: '#4a9eff', color: '#fff', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>BIG FISH</span>
              )}
              {t.add_to_main_overall && (
                <span style={{ background: '#3adc8a', color: '#0d1b2a', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>+OVERALL</span>
              )}
              {t.track_species && (
                <span style={{ background: '#2ecc71', color: '#fff', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>SPECIES</span>
              )}
            </div>
            <div style={{ fontSize: '0.77rem', color: '#8a9bb5', marginTop: 4 }}>
              {typeLabel(t)} · {scoringLabel(t)}
              {t.is_box_entry && ` · Max ${t.fish_per_box} fish/box`}
              {t.num_days > 1 && ` · ${t.num_days} days`}
            </div>
            {dayVariants.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {[t, ...dayVariants].map((d, i) => (
                  <span key={d.id} style={{ background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem', color: '#8a9bb5' }}>
                    Day {d.day_number}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, marginLeft: 12 }}>
            <button className="btn btn-ghost btn-sm" style={{ color: '#4a9eff' }} onClick={() => startEdit(t)}>
              Edit
            </button>
            <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleDelete(t.id, t.name)}>
              Delete
            </button>
          </div>
        </div>
      </div>
      {editingId === t.id && editForm && (
        <div className="card" style={{ marginTop: 4, borderColor: '#4a9eff', borderWidth: 2 }}>
          <h4 style={{ marginBottom: 20 }}>Edit Tournament</h4>
          <TournamentForm
            form={editForm}
            setForm={setEditForm}
            onSubmit={handleEdit}
            onCancel={() => { setEditingId(null); setEditForm(null); setEditError(''); }}
            submitLabel="Save Changes"
            error={editError}
            radioPrefix={`edit_${t.id}`}
            tournaments={tournaments}
            editingId={editingId}
          />
        </div>
      )}
    </div>
  );
}

function TournamentsTab({ eventId, event, tournaments, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState('');
  const emptyForm = {
    name: '',
    is_main_tournament: true,
    tournament_type: 'box_weight',
    num_days: 1,
    is_box_entry: true,
    fish_per_box: 6,
    has_big_fish_contest: false,
    scoring_mode: 'points',
    points_per_pound: 1.00,
    points_per_fish: 0,
    points_for_big_fish: 0,
    scoring_combination: 'and',
    add_to_main_overall: false,
    calcutta_tracks: 'overall',
    calcutta_day: 1,
    source_tournament_id: null,
    target_species: '',
    track_species: false,
    species_list: [],
    // Custom tournament fields
    start_date: null,
    end_date: null,
    max_total_fish: null,
    min_weight_lbs: null,
    max_entries_per_team_per_day: null,
    scoreboard_layout: 'leaderboard',
    rollover_display_mode: 'pill',
    // Scoreboard columns
    scoreboard_columns: { team_name: true, fish_count: true, day: false, species: false, weight: true, points: true, spread: false, team_number: true, big_fish: true },
    weight_decimals: 2,
  };
  const [form, setForm] = useState(emptyForm);

  function computeEffectiveColumns(f) {
    const cols = f.scoreboard_columns || {};
    const eff = { ...cols };
    const isDW = f.tournament_type === 'custom' && f.scoreboard_layout === 'daily_winners';
    if (isDW) { eff.day = true; eff.fish_count = false; }
    if ((f.num_days || 1) <= 1 && !isDW) eff.day = false;
    if (!f.has_big_fish_contest) eff.big_fish = false;
    if (!f.track_species && f.tournament_type !== 'custom') eff.species = false;
    if (f.scoring_mode === 'none') eff.points = false;
    return eff;
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...form };
      if (payload.tournament_type === 'calcutta') {
        delete payload.scoreboard_columns;
      } else {
        payload.scoreboard_columns = computeEffectiveColumns(form);
      }
      await tournamentApi.createTournament(eventId, payload);
      setShowForm(false);
      setForm(emptyForm);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create tournament');
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete "${name}"? All catch logs for this tournament will also be deleted.`)) return;
    try {
      await tournamentApi.deleteTournament(eventId, id);
      onRefresh();
    } catch (err) {
      alert('Failed to delete tournament');
    }
  }

  function startEdit(t) {
    setEditingId(t.id);
    setEditError('');
    setShowForm(false);
    setEditForm({
      name: (t.name || '').replace(/\s*\(Day\s*\d+\)\s*$/, '').trim(),
      is_main_tournament: !!t.is_main_tournament,
      tournament_type: t.tournament_type || 'box_weight',
      num_days: t.num_days || 1,
      is_box_entry: !!t.is_box_entry,
      fish_per_box: t.fish_per_box || 6,
      has_big_fish_contest: !!t.has_big_fish_contest,
      scoring_mode: t.scoring_mode || 'points',
      points_per_pound: parseFloat(t.points_per_pound) || 1.00,
      points_per_fish: parseFloat(t.points_per_fish) || 0,
      points_for_big_fish: parseFloat(t.points_for_big_fish) || 0,
      scoring_combination: t.scoring_combination || 'and',
      add_to_main_overall: !!t.add_to_main_overall,
      calcutta_tracks: t.calcutta_tracks || 'overall',
      calcutta_day: t.calcutta_day || 1,
      source_tournament_id: t.source_tournament_id || null,
      target_species: t.target_species || '',
      track_species: !!t.track_species,
      species_list: (t.species_list || []).map(s => s.species_name || s),
      // Custom tournament fields (normalize ISO dates to YYYY-MM-DD for date inputs)
      start_date: t.start_date ? t.start_date.substring(0, 10) : null,
      end_date: t.end_date ? t.end_date.substring(0, 10) : null,
      max_total_fish: t.max_total_fish || null,
      min_weight_lbs: t.min_weight_lbs ? parseFloat(t.min_weight_lbs) : null,
      max_entries_per_team_per_day: t.max_entries_per_team_per_day || null,
      scoreboard_layout: t.scoreboard_layout || 'leaderboard',
      rollover_display_mode: t.rollover_display_mode || 'pill',
      scoreboard_columns: t.scoreboard_columns || { team_name: true, fish_count: true, day: false, species: false, weight: true, points: true, spread: false, team_number: true, big_fish: true },
      weight_decimals: t.weight_decimals ?? 2,
    });
  }

  async function handleEdit(e) {
    e.preventDefault();
    setEditError('');
    try {
      const payload = { ...editForm };
      if (payload.tournament_type === 'calcutta') {
        delete payload.scoreboard_columns;
      } else {
        payload.scoreboard_columns = computeEffectiveColumns(editForm);
      }
      await tournamentApi.updateTournament(eventId, editingId, payload);
      setEditingId(null);
      setEditForm(null);
      onRefresh();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update tournament');
    }
  }

  // Separate tournaments into main and secondary, filter out day-variants (day_number > 1)
  const mainTournaments = tournaments.filter(t => t.is_main_tournament && !t.parent_tournament_id);
  const secondaryTournaments = tournaments.filter(t => !t.is_main_tournament && !t.parent_tournament_id);

  return (
    <div>
      {/* Add / Import Tournament buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => { setShowImport(true); setError(''); setEditingId(null); }}>
          Import Tournaments
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => {
          if (!showForm) {
            const hasMain = tournaments.some(t => t.is_main_tournament && t.is_active !== false && !t.parent_tournament_id);
            setForm({ ...emptyForm, is_main_tournament: !hasMain });
          }
          setShowForm(!showForm); setError(''); setEditingId(null);
        }}>
          {showForm ? 'Cancel' : '+ Add Tournament'}
        </button>
      </div>

      {showImport && (
        <ImportTournamentsModal
          eventId={eventId}
          event={event}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); onRefresh(); }}
        />
      )}

      {/* ── CREATE FORM ── */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24, borderColor: '#2a3f56' }}>
          <h4 style={{ marginBottom: 20 }}>New Tournament</h4>
          <TournamentForm
            form={form}
            setForm={setForm}
            onSubmit={handleCreate}
            onCancel={() => { setShowForm(false); setForm(emptyForm); }}
            submitLabel={`Create Tournament${form.num_days > 1 ? ` (${form.num_days} days)` : ''}`}
            error={error}
            radioPrefix="create"
            tournaments={tournaments}
          />
        </div>
      )}

      {/* ── MAIN TOURNAMENT SECTION ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ height: 2, flex: 1, background: '#e8a838', opacity: 0.4 }} />
          <span style={{ color: '#e8a838', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Main Tournament</span>
          <div style={{ height: 2, flex: 1, background: '#e8a838', opacity: 0.4 }} />
        </div>
        {mainTournaments.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '28px 0', color: '#5c6d83', fontSize: '0.88rem' }}>
            No main tournament yet. Click "+ Add Tournament" and select Main Tournament.
          </div>
        ) : (
          mainTournaments.map(t => <TournamentCard key={t.id} t={t} tournaments={tournaments} editingId={editingId} editForm={editForm} setEditForm={setEditForm} handleEdit={handleEdit} handleDelete={handleDelete} startEdit={startEdit} setEditingId={setEditingId} setEditError={setEditError} editError={editError} />)
        )}
      </div>

      {/* ── SECONDARY TOURNAMENTS SECTION ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ height: 2, flex: 1, background: '#4a9eff', opacity: 0.4 }} />
          <span style={{ color: '#4a9eff', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Secondary Tournaments</span>
          <div style={{ height: 2, flex: 1, background: '#4a9eff', opacity: 0.4 }} />
        </div>
        {secondaryTournaments.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '28px 0', color: '#5c6d83', fontSize: '0.88rem' }}>
            No secondary tournaments. Add side bets, big fish contests, or Calcutta tournaments here.
          </div>
        ) : (
          secondaryTournaments.map(t => <TournamentCard key={t.id} t={t} tournaments={tournaments} editingId={editingId} editForm={editForm} setEditForm={setEditForm} handleEdit={handleEdit} handleDelete={handleDelete} startEdit={startEdit} setEditingId={setEditingId} setEditError={setEditError} editError={editError} />)
        )}
      </div>
    </div>
  );
}

// ─── IMPORT TOURNAMENTS MODAL ────────────────────────────────
function ImportTournamentsModal({ eventId, event, onClose, onImported }) {
  const normType = (t) => (t === 'individual' ? 'individual' : 'team');
  const targetType = normType(event?.event_type);
  const targetTypeLabel = targetType === 'individual' ? 'Participant' : 'Team';

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [sourceTournaments, setSourceTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(false);
  const [checked, setChecked] = useState({});
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  // Load candidate events: same type, excluding the current event
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await eventsApi.getEvents();
        const list = (res.data?.data || res.data || []).filter(
          ev => ev.id !== eventId && normType(ev.event_type) === targetType
        );
        if (active) setEvents(list);
      } catch (err) {
        if (active) setError('Failed to load events');
      } finally {
        if (active) setLoadingEvents(false);
      }
    })();
    return () => { active = false; };
  }, [eventId, targetType]);

  // Load tournaments for the selected source event
  useEffect(() => {
    if (!selectedEventId) { setSourceTournaments([]); setChecked({}); return; }
    let active = true;
    setLoadingTournaments(true);
    setChecked({});
    (async () => {
      try {
        const res = await tournamentApi.getTournaments(selectedEventId);
        const all = res.data?.data || res.data || [];
        // Only parent tournaments (exclude day variants), active only
        const parents = all.filter(t => !t.parent_tournament_id && t.is_active !== false);
        if (active) setSourceTournaments(parents);
      } catch (err) {
        if (active) { setSourceTournaments([]); setError('Failed to load tournaments for that event'); }
      } finally {
        if (active) setLoadingTournaments(false);
      }
    })();
    return () => { active = false; };
  }, [selectedEventId]);

  const selectedIds = Object.keys(checked).filter(id => checked[id]);

  function toggle(id) {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  async function handleImport() {
    if (selectedIds.length === 0) return;
    setImporting(true);
    setError('');
    try {
      // Import in display order
      const ordered = sourceTournaments.filter(t => checked[t.id]).map(t => t.id);
      await tournamentApi.importTournaments(eventId, ordered);
      onImported();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import tournaments');
      setImporting(false);
    }
  }

  const typeLabels = { box_weight: 'Box Weight', heaviest_single: 'Heaviest Single', calcutta: 'Calcutta', custom: 'Custom' };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 16, width: '100%', maxWidth: 640, padding: '28px 32px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 700, color: '#f0f2f5', letterSpacing: 1 }}>Import Tournaments</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8a9bb5', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ color: '#8a9bb5', fontSize: '0.85rem', marginBottom: 20 }}>
          Copy tournament configurations from another <strong style={{ color: '#e8a838' }}>{targetTypeLabel}</strong> event into this one. Only {targetTypeLabel.toLowerCase()} events are shown.
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Event selector */}
        <div style={{ marginBottom: 20 }}>
          <label className="form-label" style={{ display: 'block', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Source Event</label>
          {loadingEvents ? (
            <div style={{ color: '#5c6d83', fontSize: '0.88rem' }}>Loading events…</div>
          ) : events.length === 0 ? (
            <div style={{ color: '#5c6d83', fontSize: '0.88rem' }}>No other {targetTypeLabel.toLowerCase()} events to import from.</div>
          ) : (
            <select
              className="form-input"
              value={selectedEventId}
              onChange={e => setSelectedEventId(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">— Select an event —</option>
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}{ev.tournament_count != null ? ` (${ev.tournament_count} tournament${ev.tournament_count === 1 ? '' : 's'})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Tournament checklist */}
        {selectedEventId && (
          <div style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ display: 'block', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Tournaments to Import</label>
            {loadingTournaments ? (
              <div style={{ color: '#5c6d83', fontSize: '0.88rem' }}>Loading tournaments…</div>
            ) : sourceTournaments.length === 0 ? (
              <div style={{ color: '#5c6d83', fontSize: '0.88rem' }}>That event has no tournaments to import.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sourceTournaments.map(t => (
                  <label
                    key={t.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: checked[t.id] ? 'rgba(232,168,56,0.08)' : '#13243a', border: `1px solid ${checked[t.id] ? '#e8a838' : '#2a3f56'}`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }}
                  >
                    <input type="checkbox" checked={!!checked[t.id]} onChange={() => toggle(t.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    <span style={{ flex: 1, color: '#f0f2f5', fontWeight: 500, fontSize: '0.92rem' }}>{t.name}</span>
                    {t.is_main_tournament && (
                      <span style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#e8a838', background: 'rgba(232,168,56,0.12)', padding: '2px 8px', borderRadius: 4 }}>Main</span>
                    )}
                    <span style={{ fontSize: '0.72rem', color: '#8a9bb5', background: 'rgba(74,158,255,0.12)', padding: '2px 8px', borderRadius: 4 }}>{typeLabels[t.tournament_type] || t.tournament_type}</span>
                    {t.num_days > 1 && (
                      <span style={{ fontSize: '0.72rem', color: '#8a9bb5' }}>{t.num_days} days</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={importing}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleImport} disabled={importing || selectedIds.length === 0}>
            {importing ? 'Importing…' : `Import${selectedIds.length ? ` ${selectedIds.length}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CATCH LOGS TAB ──────────────────────────────────────────
function WeighInTab({ eventId, event, tournaments, registrations, teams, onRefresh }) {
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [form, setForm] = useState({
    team_id: '', participant_id: '', species: '', weight_lbs: '', biggest_fish_weight: '',
    fish_count: '', manual_score: '', notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [catches, setCatches] = useState([]);
  const [allCatches, setAllCatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCatch, setEditingCatch] = useState(null);

  const [enrolledParticipants, setEnrolledParticipants] = useState([]);
  const isIndividualEvent = event?.event_type === 'individual';

  const selectedTournament = tournaments.find(t => t.id === selectedTournamentId);
  const isBox = selectedTournament?.tournament_type === 'box_weight';
  const isSingle = selectedTournament?.tournament_type === 'heaviest_single';
  const isCalcutta = selectedTournament?.tournament_type === 'calcutta';
  const isCustom = selectedTournament?.tournament_type === 'custom';
  const hasBigFish = selectedTournament?.has_big_fish_contest;
  const isManual = selectedTournament?.scoring_mode === 'manual';
  const fishPerBox = selectedTournament?.fish_per_box || 6;
  const trackSpecies = selectedTournament?.track_species;
  const speciesList = selectedTournament?.species_list || [];

  // Teams enrolled in the selected tournament (or its parent for multi-day variants)
  const enrolledTeams = teams.filter(t => {
    if (!selectedTournamentId) return true;
    const ids = t.tournament_ids || [];
    if (ids.includes(selectedTournamentId)) return true;
    if (selectedTournament?.parent_tournament_id) {
      return ids.includes(selectedTournament.parent_tournament_id);
    }
    return false;
  });

  // All confirmed registrations for the event — backend already scopes to confirmed status,
  // so show everyone regardless of tournament_entries to avoid missing participants who were
  // approved before auto-enrollment ran or for newly created tournaments.
  const enrolledParticipantsList = enrolledParticipants;

  // Fetch enrolled participants for individual events
  useEffect(() => {
    if (eventId && isIndividualEvent) {
      tournamentApi.getEnrolledParticipants(eventId)
        .then(res => setEnrolledParticipants(res.data.data || []))
        .catch(() => {});
    }
  }, [eventId, isIndividualEvent]);

  // Fetch catches when tournament changes
  useEffect(() => {
    if (eventId) fetchAllCatches();
  }, [eventId]);

  useEffect(() => {
    if (selectedTournamentId) {
      fetchTournamentCatches(selectedTournamentId);
    } else {
      setCatches([]);
    }
    setForm({ team_id: '', participant_id: '', species: '', weight_lbs: '', biggest_fish_weight: '', fish_count: '', manual_score: '', notes: '' });
    setEditingCatch(null);
  }, [selectedTournamentId]);

  async function fetchAllCatches() {
    try {
      const { data } = await tournamentApi.getCatches(eventId);
      setAllCatches(data.data || []);
    } catch (err) { console.error('Failed to fetch catches'); }
  }

  async function fetchTournamentCatches(tid) {
    try {
      const { data } = await tournamentApi.getCatches(eventId, { tournament_id: tid });
      setCatches(data.data || []);
    } catch (err) { console.error('Failed to fetch tournament catches'); }
  }

  async function handleLog(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);

    const entryId = isIndividualEvent ? form.participant_id : form.team_id;

    // Check for duplicate catch (same team/participant + same tournament)
    const existingCatch = isIndividualEvent
      ? catches.find(c => c.participant_id === form.participant_id)
      : catches.find(c => c.team_id === form.team_id);
    if (existingCatch) {
      const entryName = isIndividualEvent
        ? (enrolledParticipants.find(p => p.id === form.participant_id)?.first_name + ' ' + enrolledParticipants.find(p => p.id === form.participant_id)?.last_name || 'this participant')
        : (teams.find(t => t.id === form.team_id)?.team_name || 'this team');
      const confirmed = window.confirm(
        `A catch log already exists for "${entryName}" in this tournament.\n\nDo you want to create a duplicate entry?`
      );
      if (!confirmed) {
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        tournament_id: selectedTournamentId,
        weight_lbs: parseFloat(form.weight_lbs),
        entry_type: isBox ? 'box' : 'single',
        fish_count: form.fish_count ? parseInt(form.fish_count) : (isBox ? fishPerBox : 1),
        notes: form.notes || null,
        species: form.species || null,
      };
      if (isIndividualEvent) {
        payload.participant_id = form.participant_id;
      } else {
        payload.team_id = form.team_id;
      }
      if (hasBigFish && form.biggest_fish_weight) {
        payload.biggest_fish_weight = parseFloat(form.biggest_fish_weight);
      }
      if (isManual) {
        payload.manual_score = parseFloat(form.manual_score) || 0;
      }

      await tournamentApi.logCatch(eventId, payload);
      setSuccess('✓ Catch logged!');
      setForm({ team_id: form.team_id, participant_id: form.participant_id, species: '', weight_lbs: '', biggest_fish_weight: '', fish_count: '', manual_score: '', notes: '' });
      fetchTournamentCatches(selectedTournamentId);
      fetchAllCatches();
      onRefresh();
      if (isIndividualEvent) {
        tournamentApi.getEnrolledParticipants(eventId).then(res => setEnrolledParticipants(res.data.data || [])).catch(() => {});
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Rule violation override flow (custom tournaments)
      if (err.response?.status === 409 && err.response?.data?.violations) {
        const violations = err.response.data.violations;
        const msg = violations.map(v => `• ${v.message}`).join('\n');
        if (window.confirm(`Rule violations detected:\n\n${msg}\n\nOverride and log anyway?`)) {
          try {
            await tournamentApi.logCatch(eventId, { ...payload, rule_override: true });
            setSuccess('✓ Catch logged (rule override)');
            setForm({ team_id: form.team_id, participant_id: form.participant_id, species: '', weight_lbs: '', biggest_fish_weight: '', fish_count: '', manual_score: '', notes: '' });
            fetchTournamentCatches(selectedTournamentId);
            fetchAllCatches();
            onRefresh();
            setTimeout(() => setSuccess(''), 3000);
          } catch (err2) {
            setError(err2.response?.data?.message || 'Failed to log catch');
          }
        } else {
          setError('Catch not logged — rule violation not overridden');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to log catch');
      }
    } finally { setLoading(false); }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await tournamentApi.updateCatch(eventId, editingCatch.id, {
        weight_lbs: parseFloat(form.weight_lbs),
        biggest_fish_weight: form.biggest_fish_weight ? parseFloat(form.biggest_fish_weight) : null,
        fish_count: parseInt(form.fish_count) || 1,
        manual_score: isManual ? parseFloat(form.manual_score) || null : null,
        species: form.species || null,
        notes: form.notes || null
      });
      setSuccess('✓ Updated!');
      setEditingCatch(null);
      setForm({ team_id: '', species: '', weight_lbs: '', biggest_fish_weight: '', fish_count: '', manual_score: '', notes: '' });
      fetchTournamentCatches(selectedTournamentId);
      fetchAllCatches();
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update catch');
    } finally { setLoading(false); }
  }

  async function handleDelete(catchId) {
    if (!window.confirm('Delete this catch? Cannot be undone.')) return;
    try {
      await tournamentApi.deleteCatch(eventId, catchId);
      fetchTournamentCatches(selectedTournamentId);
      fetchAllCatches();
      onRefresh();
    } catch (err) { alert('Failed to delete catch'); }
  }

  function startEdit(c) {
    setEditingCatch(c);
    setForm({
      team_id: c.team_id,
      species: c.species || '',
      weight_lbs: c.weight_lbs?.toString() || '',
      biggest_fish_weight: c.biggest_fish_weight?.toString() || '',
      fish_count: c.fish_count?.toString() || '',
      manual_score: c.manual_score?.toString() || '',
      notes: c.notes || ''
    });
  }

  const [teamSort, setTeamSort] = useState('number'); // 'number' | 'name'

  const sortedEnrolledTeams = [...enrolledTeams].sort((a, b) => {
    if (teamSort === 'name') return (a.team_name || '').localeCompare(b.team_name || '');
    return (a.team_number || 9999) - (b.team_number || 9999);
  });

  const sortedEnrolledParticipants = [...enrolledParticipantsList].sort((a, b) => {
    if (teamSort === 'name') {
      return `${a.first_name || ''} ${a.last_name || ''}`.trim()
        .localeCompare(`${b.first_name || ''} ${b.last_name || ''}`.trim());
    }
    return (a.participant_number || 9999) - (b.participant_number || 9999);
  });

  const displayCatches = catches;

  return (
    <div>
      {/* ── Tournament selector at top ── */}
      <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
        <div className="tournament-selector" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontWeight: 600, color: '#8a9bb5', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            Tournament
          </div>
          <select
            value={selectedTournamentId}
            onChange={e => setSelectedTournamentId(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— Select a tournament to log catches —</option>
            {/* Group: main */}
            {tournaments.filter(t => t.is_main_tournament).length > 0 && (
              <optgroup label="── Main Tournament">
                {tournaments.filter(t => t.is_main_tournament).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            )}
            {/* Group: secondary non-calcutta */}
            {tournaments.filter(t => !t.is_main_tournament && t.tournament_type !== 'calcutta').length > 0 && (
              <optgroup label="── Secondary Tournaments">
                {tournaments.filter(t => !t.is_main_tournament && t.tournament_type !== 'calcutta').map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            )}
            {/* Group: calcutta */}
            {tournaments.filter(t => t.tournament_type === 'calcutta').length > 0 && (
              <optgroup label="── Calcutta (auto-calculated)">
                {tournaments.filter(t => t.tournament_type === 'calcutta').map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            )}
          </select>
          {selectedTournamentId && (
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
              {selectedTournament?.tournament_type === 'box_weight' && <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>BOX</span>}
              {selectedTournament?.tournament_type === 'heaviest_single' && <span style={{ background: '#4a9eff', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>SINGLE</span>}
              {isCustom && <span style={{ background: '#4a9eff', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>CUSTOM</span>}
              {isCalcutta && <span style={{ background: '#9b59b6', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>CALCUTTA</span>}
              {isManual && <span style={{ background: '#3adc8a', color: '#0d1b2a', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>MANUAL</span>}
              {selectedTournament?.scoring_mode === 'none' && <span style={{ background: '#5c6d83', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>WT ONLY</span>}
              {isCustom && selectedTournament?.day_number > 0 && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: '0.7rem', padding: '3px 10px', color: selectedTournament?.is_rolled_over ? '#ff9f0a' : '#8a9bb5' }}
                  onClick={async () => {
                    const newVal = !selectedTournament?.is_rolled_over;
                    try {
                      await tournamentApi.toggleRollover(eventId, selectedTournamentId, newVal);
                      onRefresh();
                    } catch (e) { /* ignore */ }
                  }}
                >
                  {selectedTournament?.is_rolled_over ? '● ROLLOVER' : 'Mark Rollover'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!selectedTournamentId ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 0', color: '#5c6d83' }}>
          <div style={{ marginBottom: 12, color: '#5c6d83' }}><IconScale size="32px" /></div>
          <div style={{ fontSize: '0.9rem' }}>Select a tournament above to begin logging catches</div>
        </div>
      ) : isCalcutta ? (
        /* ── Calcutta: no log form ── */
        <div className="card" style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 12, color: '#9b59b6' }}><IconTarget size="32px" /></div>
          <div style={{ color: '#9b59b6', fontWeight: 600, marginBottom: 8 }}>Calcutta — Auto-Calculated</div>
          <div style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>
            Rankings for <strong>{selectedTournament?.name}</strong> are derived automatically from the Main Event results.
            <br />No catch logs needed for this tournament.
          </div>
          <div style={{ color: '#5c6d83', fontSize: '0.78rem', marginTop: 12 }}>
            Tracks: {selectedTournament?.calcutta_tracks?.replace(/_/g, ' ')}
          </div>
        </div>
      ) : (
        <div className="weighin-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 20 }}>

          {/* ── Log / Edit form ── */}
          <div className="card">
            <h4 style={{ marginBottom: 16 }}>
              {editingCatch
                ? <><IconEdit size="15px" style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} />Edit Entry</>
                : isBox
                  ? <><IconBox size="15px" style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} />Log Box</>
                  : <><IconFish size="15px" style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} />Log Fish</>
              }
            </h4>
            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
            {success && <div className="alert alert-success" style={{ marginBottom: 12 }}>{success}</div>}

            <form onSubmit={editingCatch ? handleUpdate : handleLog}>

              {/* Team / Participant */}
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label style={{ margin: 0 }}>{isIndividualEvent ? 'Participant' : 'Team'} {!isIndividualEvent && enrolledTeams.length < teams.length && <span style={{ color: '#5c6d83', fontSize: '0.72rem' }}>({enrolledTeams.length} enrolled)</span>}{isIndividualEvent && <span style={{ color: '#5c6d83', fontSize: '0.72rem' }}>({enrolledParticipantsList.length} enrolled)</span>}</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" onClick={() => setTeamSort('number')}
                      style={{ padding: '2px 8px', fontSize: '0.68rem', fontWeight: 600, borderRadius: 4, border: '1px solid #2a3f56', cursor: 'pointer',
                        background: teamSort === 'number' ? '#e8a838' : 'transparent', color: teamSort === 'number' ? '#0d1b2a' : '#5c6d83' }}>
                      #
                    </button>
                    <button type="button" onClick={() => setTeamSort('name')}
                      style={{ padding: '2px 8px', fontSize: '0.68rem', fontWeight: 600, borderRadius: 4, border: '1px solid #2a3f56', cursor: 'pointer',
                        background: teamSort === 'name' ? '#e8a838' : 'transparent', color: teamSort === 'name' ? '#0d1b2a' : '#5c6d83' }}>
                      A-Z
                    </button>
                  </div>
                </div>
                {isIndividualEvent ? (
                  <select value={form.participant_id} onChange={e => setForm({ ...form, participant_id: e.target.value })} required disabled={!!editingCatch}>
                    <option value="">Select participant...</option>
                    {sortedEnrolledParticipants.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.participant_number ? `#${p.participant_number} ` : ''}{p.first_name} {p.last_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select value={form.team_id} onChange={e => setForm({ ...form, team_id: e.target.value })} required disabled={!!editingCatch}>
                    <option value="">Select team...</option>
                    {sortedEnrolledTeams.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.team_number ? `#${t.team_number} ` : ''}{t.team_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* FISH SPECIES (when track_species enabled) */}
              {trackSpecies && speciesList.length > 0 && (
                <div className="form-group">
                  <label>Fish Species</label>
                  <select value={form.species} onChange={e => setForm({ ...form, species: e.target.value })} required>
                    <option value="">Select species...</option>
                    {speciesList.map((s, i) => (
                      <option key={i} value={s.species_name || s}>{s.species_name || s}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* BOX WEIGHT */}
              {isBox && (
                <>
                  {hasBigFish && (
                    <div className="form-group">
                      <label>Biggest Fish Weight (lbs)</label>
                      <input type="number" step="0.001" min="0" value={form.biggest_fish_weight}
                        onChange={e => setForm({ ...form, biggest_fish_weight: e.target.value })}
                        placeholder="e.g. 15.250" />
                      <small style={{ color: '#4a9eff', display: 'block', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><IconTrophy size="12px" /> Counted for Big Fish contest</small>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Total Box Weight (lbs)</label>
                    <input type="number" step="0.001" min="0.001" value={form.weight_lbs}
                      onChange={e => setForm({ ...form, weight_lbs: e.target.value })}
                      placeholder="e.g. 45.750" required />
                    {hasBigFish && <small style={{ color: '#5c6d83', display: 'block', marginTop: 3 }}>Includes the biggest fish</small>}
                  </div>
                  <div className="form-group">
                    <label>Fish Count <span style={{ color: '#5c6d83', fontSize: '0.72rem' }}>(Max {fishPerBox})</span></label>
                    <input type="number" min="1" max={fishPerBox} value={form.fish_count}
                      onChange={e => setForm({ ...form, fish_count: e.target.value })}
                      placeholder={fishPerBox.toString()} />
                    <small style={{ color: '#5c6d83', display: 'block', marginTop: 3 }}>Record-keeping only · Teams over limit are disqualified</small>
                  </div>
                </>
              )}

              {/* HEAVIEST SINGLE */}
              {isSingle && (
                <div className="form-group">
                  <label>Fish Weight (lbs)</label>
                  <input type="number" step="0.001" min="0.001" value={form.weight_lbs}
                    onChange={e => setForm({ ...form, weight_lbs: e.target.value })}
                    placeholder="e.g. 22.500" required />
                </div>
              )}

              {/* CUSTOM / WEIGHT-ONLY (non-box, non-single) */}
              {!isBox && !isSingle && !isManual && !isCalcutta && (
                <div className="form-group">
                  <label>Fish Weight (lbs)</label>
                  <input type="number" step="0.001" min="0.001" value={form.weight_lbs}
                    onChange={e => setForm({ ...form, weight_lbs: e.target.value })}
                    placeholder="e.g. 22.500" required />
                </div>
              )}

              {/* Manual score */}
              {isManual && (
                <div className="form-group">
                  <label>Score</label>
                  <input type="number" step="0.01" value={form.manual_score}
                    onChange={e => setForm({ ...form, manual_score: e.target.value })}
                    placeholder="Enter score" required />
                  <small style={{ color: '#3adc8a', display: 'block', marginTop: 3 }}>Manual — this score is final</small>
                </div>
              )}

              {/* Notes */}
              <div className="form-group">
                <label>Notes <span style={{ color: '#5c6d83', fontSize: '0.72rem' }}>(optional)</span></label>
                <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                  {loading ? 'Saving...' : editingCatch ? 'Update' : 'Log'}
                </button>
                {editingCatch && (
                  <button type="button" className="btn btn-ghost" onClick={() => { setEditingCatch(null); setForm({ team_id: '', species: '', weight_lbs: '', biggest_fish_weight: '', fish_count: '', manual_score: '', notes: '' }); }}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* ── Catch log history ── */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h4 style={{ margin: 0 }}>Catch Log</h4>
              <span style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>{displayCatches.length} entr{displayCatches.length !== 1 ? 'ies' : 'y'}</span>
            </div>
            {displayCatches.length === 0 ? (
              <div style={{ color: '#5c6d83', textAlign: 'center', padding: '32px 0', fontSize: '0.85rem' }}>
                No catches logged yet for this tournament
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 520, overflowY: 'auto' }}>
                {displayCatches.map(c => {
                  const scoreDisplay = c.manual_score != null
                    ? `${c.manual_score} pts (manual)`
                    : selectedTournament?.scoring_mode === 'none'
                      ? `${c.weight_lbs} lbs`
                      : `${c.points_earned} pts`;

                  return (
                    <div key={c.id} className="catch-entry" style={{ background: '#111f2e', borderRadius: 8, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, flexWrap: 'wrap' }}>
                          {c.participant_id ? (
                            <>
                              {c.participant_number && (
                                <span style={{ color: '#e8a838', fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '0.8rem' }}>#{c.participant_number}</span>
                              )}
                              <span style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>
                                {c.participant_first_name} {c.participant_last_name}
                              </span>
                            </>
                          ) : (
                            <>
                              {c.team_number && (
                                <span style={{ color: '#e8a838', fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '0.8rem' }}>#{c.team_number}</span>
                              )}
                              <span style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{c.team_name}</span>
                            </>
                          )}
                          {c.entry_type === 'box' && <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: 3 }}>BOX</span>}
                        </div>
                        <div style={{ color: '#8a9bb5', fontSize: '0.75rem' }}>
                          {c.weight_lbs} lbs total
                          {c.biggest_fish_weight && <span style={{ color: '#4a9eff' }}> · BF: {c.biggest_fish_weight} lbs</span>}
                          {c.fish_count > 1 && ` · ${c.fish_count} fish`}
                          {c.species && <span style={{ color: '#2ecc71' }}> · {c.species}</span>}
                        </div>
                        {c.notes && <div style={{ color: '#5c6d83', fontSize: '0.7rem', marginTop: 3, fontStyle: 'italic' }}>{c.notes}</div>}
                        {!c.is_valid && <div style={{ color: '#e85d5d', fontSize: '0.7rem', marginTop: 3 }}>⛔ Invalidated</div>}
                      </div>
                      <div className="catch-entry-score" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginLeft: 10 }}>
                        <div style={{ color: c.manual_score != null ? '#3adc8a' : '#e8a838', fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                          {scoreDisplay}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '3px 8px', fontSize: '0.7rem', color: '#4a9eff' }} onClick={() => startEdit(c)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '3px 8px', fontSize: '0.7rem', color: '#e85d5d' }} onClick={() => handleDelete(c.id)}>Del</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
// ─── REGISTRATIONS TAB ───────────────────────────────────────

// ─── AUTOCOMPLETE HOOK ────────────────────────────────────────
function useAutocomplete(searchFn, minChars = 1) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showList, setShowList] = useState(false);
  const timerRef = useRef(null);

  function handleChange(val) {
    setQuery(val);
    setShowList(true);
    clearTimeout(timerRef.current);
    if (val.length < minChars) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await searchFn(val);
        setResults(data.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
  }

  function reset() { setQuery(''); setResults([]); setShowList(false); }

  return { query, setQuery, results, loading, showList, setShowList, handleChange, reset };
}

// ─── PARTICIPANTS TAB (replaces Registrations) ────────────────
function RegistrationsTab({ registrations, eventId, event, teams, tournaments, onRefresh, highlightUserId, clearHighlight }) {
  const isTeamEvent = event?.event_type !== 'individual';
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [expandedId] = useState(null); // kept for compat; accordion removed
  const [participantSort, setParticipantSort] = useState('number'); // 'number' or 'name'

  // Invite URL state
  const inviteUrl = `${window.location.origin}/public/events/${eventId}`;
  const [linkCopied, setLinkCopied] = useState(false);
  function copyInviteLink() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(inviteUrl).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      });
    } else {
      const el = document.createElement('textarea');
      el.value = inviteUrl;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }

  // Pending registrations state
  const [pendingOpen, setPendingOpen] = useState(true);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // registrationId being acted on
  const [pendingError, setPendingError] = useState('');

  useEffect(() => {
    setPendingLoading(true);
    eventsApi.getRegistrations(eventId)
      .then(res => setPendingRegs((res.data.data || []).filter(r => r.registration_status === 'pending')))
      .catch(() => {})
      .finally(() => setPendingLoading(false));
  }, [eventId, registrations]);

  async function handlePendingAction(regId, status) {
    setPendingAction(regId);
    setPendingError('');
    try {
      await eventsApi.updateRegistrationStatus(eventId, regId, status);
      setPendingRegs(prev => prev.filter(r => r.id !== regId));
      if (status === 'confirmed') onRefresh();
    } catch (err) {
      setPendingError(err.response?.data?.message || 'Action failed');
    } finally {
      setPendingAction(null);
    }
  }

  const userAC = useAutocomplete(q => searchApi.searchUsers(q));

  // Bulk add state
  const [showBulkAdd, setShowBulkAdd]   = useState(false);
  const [allUsers, setAllUsers]         = useState([]);
  const [bulkSearchQ, setBulkSearchQ]   = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError]       = useState('');
  const [bulkSuccess, setBulkSuccess]   = useState('');
  const bulkSearchTimer = useRef(null);

  function selectExistingUser(u) {
    setForm({ first_name: u.first_name, last_name: u.last_name, email: u.email, phone: u.phone || '' });
    userAC.setQuery(`${u.first_name} ${u.last_name}`);
    userAC.setShowList(false);
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      setError('All fields are required'); return;
    }
    setSaving(true); setError('');
    try {
      await searchApi.addParticipant(eventId, form);
      setShowForm(false);
      setForm({ first_name: '', last_name: '', email: '', phone: '' });
      userAC.reset();
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add participant');
    } finally { setSaving(false); }
  }

  // Fetch all system users when bulk-add panel opens
  useEffect(() => {
    if (!showBulkAdd) return;
    setLoadingUsers(true);
    searchApi.getAllParticipants(eventId, '')
      .then(res => setAllUsers(res.data.data || []))
      .catch(() => setAllUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [showBulkAdd, eventId]);

  function handleBulkSearch(val) {
    setBulkSearchQ(val);
    clearTimeout(bulkSearchTimer.current);
    bulkSearchTimer.current = setTimeout(() => {
      setLoadingUsers(true);
      searchApi.getAllParticipants(eventId, val)
        .then(res => setAllUsers(res.data.data || []))
        .catch(() => setAllUsers([]))
        .finally(() => setLoadingUsers(false));
    }, 300);
  }

  function toggleUserSelect(userId) {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }

  const availableUsers = allUsers.filter(u => !u.already_in_event);
  const alreadyRegistered = allUsers.filter(u => u.already_in_event);

  function selectAllUsers() {
    if (selectedUsers.size === availableUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(availableUsers.map(u => u.id)));
    }
  }

  async function handleBulkAddParticipants() {
    if (selectedUsers.size === 0) return;
    setBulkSubmitting(true);
    setBulkError('');
    setBulkSuccess('');
    try {
      const res = await searchApi.bulkAddParticipants(eventId, { user_ids: [...selectedUsers] });
      const { created, skipped } = res.data.data;
      let msg = `${created.length} participant(s) added.`;
      if (skipped.length > 0) msg += ` ${skipped.length} skipped (already registered).`;
      setBulkSuccess(msg);
      setSelectedUsers(new Set());
      onRefresh();
      searchApi.getAllParticipants(eventId, bulkSearchQ)
        .then(r => setAllUsers(r.data.data || []))
        .catch(() => {});
    } catch (err) {
      setBulkError(err.response?.data?.message || 'Failed to add participants');
    } finally {
      setBulkSubmitting(false);
    }
  }

  // Build team membership lookup for display
  const teamByUserId = {};
  teams.forEach(t => {
    (t.members || []).forEach(m => {
      teamByUserId[m.id] = { team_name: t.team_name, team_number: t.team_number, is_captain: m.role === 'captain' };
    });
  });

  return (
    <div>
      {/* ── Invite Link ─────────────────────────────────────── */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ color: '#8a9bb5', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Participant Invite Link</div>
        <div style={{ fontSize: '0.78rem', color: '#8a9bb5', marginBottom: 12 }}>
          Share this link with participants so they can sign up and register for this event.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0, background: 'rgba(0,0,0,0.2)', border: '1px solid #2a3f56', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', color: '#8a9bb5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {inviteUrl}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={copyInviteLink} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {linkCopied ? '✓ Copied!' : '⧉ Copy Link'}
          </button>
        </div>
      </div>

      {/* ── Pending Registrations (individual events only) ────── */}
      {!isTeamEvent && (pendingLoading || pendingRegs.length > 0) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <button
            onClick={() => setPendingOpen(o => !o)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 20px', color: '#f0f2f5' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Pending Registration Requests</span>
              {pendingRegs.length > 0 && (
                <span style={{ background: '#e8a838', color: '#000', borderRadius: 12, padding: '1px 9px', fontSize: '0.72rem', fontWeight: 700 }}>{pendingRegs.length}</span>
              )}
            </div>
            <span style={{ color: '#5c6d83', fontSize: '0.8rem' }}>{pendingOpen ? '▲ Hide' : '▼ Show'}</span>
          </button>

          {pendingOpen && (
            <div style={{ padding: '0 20px 16px' }}>
              {pendingError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{pendingError}</div>}
              {pendingLoading ? (
                <div className="spinner" />
              ) : pendingRegs.length === 0 ? (
                <div style={{ color: '#5c6d83', fontSize: '0.82rem', textAlign: 'center', padding: '16px 0' }}>No pending requests.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pendingRegs.map(r => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: 'rgba(0,0,0,0.15)', borderRadius: 8, border: '1px solid #2a3f56', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#f0f2f5', fontSize: '0.88rem' }}>{r.first_name} {r.last_name}</div>
                        <div style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>{r.email}</div>
                        <div style={{ color: '#5c6d83', fontSize: '0.72rem', marginTop: 2 }}>
                          Requested {new Date(r.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handlePendingAction(r.id, 'confirmed')}
                          disabled={pendingAction === r.id}
                        >
                          {pendingAction === r.id ? '...' : 'Approve'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handlePendingAction(r.id, 'cancelled')}
                          disabled={pendingAction === r.id}
                          style={{ color: '#e85d5d' }}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>
            {registrations.length} participant{registrations.length !== 1 ? 's' : ''}
            {isTeamEvent && ' · Team event'}
          </span>
          {registrations.length > 1 && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-ghost btn-sm" style={{ padding: '4px 10px', fontSize: '0.72rem', color: participantSort === 'number' ? '#e8a838' : '#5c6d83', background: participantSort === 'number' ? 'rgba(232,168,56,0.1)' : 'transparent' }} onClick={() => setParticipantSort('number')}>#</button>
              <button className="btn btn-ghost btn-sm" style={{ padding: '4px 10px', fontSize: '0.72rem', color: participantSort === 'name' ? '#e8a838' : '#5c6d83', background: participantSort === 'name' ? 'rgba(232,168,56,0.1)' : 'transparent' }} onClick={() => setParticipantSort('name')}>A–Z</button>
            </div>
          )}
        </div>
        {!isTeamEvent && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowBulkAdd(!showBulkAdd); setBulkError(''); setBulkSuccess(''); if (showForm) setShowForm(false); }}>
              {showBulkAdd ? 'Cancel' : '+ Add Participants'}
            </button>
          </div>
        )}
      </div>

      {isTeamEvent && (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px', marginBottom: 20, color: '#8a9bb5' }}>
          <div style={{ marginBottom: 8, color: '#8a9bb5' }}><IconTrophy size="32px" /></div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#f0f2f5', marginBottom: 6 }}>Team Event</div>
          <div style={{ fontSize: '0.82rem' }}>This is a team event. Participants are added through team membership — add teams on the Teams tab and assign captains/members there.</div>
        </div>
      )}

      {/* Bulk add participants panel */}
      {!isTeamEvent && showBulkAdd && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 4 }}>Add Participants from System</h4>
          <p style={{ color: '#8a9bb5', fontSize: '0.8rem', marginBottom: 16 }}>Select participants to register for this event.</p>

          {bulkError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{bulkError}</div>}
          {bulkSuccess && <div className="alert alert-success" style={{ marginBottom: 12, background: 'rgba(46,204,113,0.1)', border: '1px solid #2ecc71', color: '#2ecc71', padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem' }}>{bulkSuccess}</div>}

          <div className="form-group" style={{ marginBottom: 12 }}>
            <input
              value={bulkSearchQ}
              onChange={e => handleBulkSearch(e.target.value)}
              placeholder="Search by name, email or phone..."
              autoComplete="off"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#8a9bb5', fontSize: '0.82rem' }}>
              <input
                type="checkbox"
                checked={availableUsers.length > 0 && selectedUsers.size === availableUsers.length}
                onChange={selectAllUsers}
                style={{ accentColor: '#4a9eff', width: 16, height: 16 }}
              />
              Select all available ({availableUsers.length})
            </label>
            {selectedUsers.size > 0 && (
              <span style={{ color: '#4a9eff', fontSize: '0.82rem', fontWeight: 600 }}>{selectedUsers.size} selected</span>
            )}
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #2a3f56', borderRadius: 8, marginBottom: 16 }}>
            {loadingUsers ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#8a9bb5' }}>Loading participants...</div>
            ) : allUsers.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#8a9bb5' }}>No participants found{bulkSearchQ ? ` matching "${bulkSearchQ}"` : ''}.</div>
            ) : (
              <>
                {availableUsers.map(u => {
                  const isSelected = selectedUsers.has(u.id);
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1e2d3d', background: isSelected ? 'rgba(74,158,255,0.06)' : 'transparent' }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#1a2a3a'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? 'rgba(74,158,255,0.06)' : 'transparent'; }}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => toggleUserSelect(u.id)} style={{ accentColor: '#4a9eff', width: 16, height: 16, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{u.first_name} {u.last_name}</span>
                        <span style={{ color: '#8a9bb5', fontSize: '0.72rem', marginLeft: 8 }}>{u.email}</span>
                      </div>
                      <span style={{ color: '#5c6d83', fontSize: '0.7rem', flexShrink: 0 }}>{u.events_entered} event{u.events_entered !== 1 ? 's' : ''}</span>
                    </label>
                  );
                })}
                {alreadyRegistered.length > 0 && (
                  <>
                    <div style={{ padding: '8px 14px', background: '#111f2e', color: '#5c6d83', fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid #1e2d3d' }}>Already registered</div>
                    {alreadyRegistered.map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #1e2d3d', opacity: 0.5 }}>
                        <input type="checkbox" disabled checked style={{ width: 16, height: 16, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>{u.first_name} {u.last_name}</span>
                          <span style={{ color: '#5c6d83', fontSize: '0.72rem', marginLeft: 8 }}>{u.email}</span>
                        </div>
                        <span style={{ color: '#5c6d83', fontSize: '0.7rem', flexShrink: 0 }}>already added</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={handleBulkAddParticipants} disabled={selectedUsers.size === 0 || bulkSubmitting}>
              {bulkSubmitting ? 'Adding...' : `Add ${selectedUsers.size} Participant${selectedUsers.size !== 1 ? 's' : ''}`}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowBulkAdd(false); setSelectedUsers(new Set()); setBulkSearchQ(''); }}>Cancel</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" style={{ color: '#e8a838' }} onClick={() => { setShowForm(!showForm); setError(''); }}>
              {showForm ? 'Hide' : '+ Add Participant'}
            </button>
          </div>

          {/* Manual add single participant form */}
          {showForm && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid #e8a838', borderRadius: 8, background: 'rgba(232,168,56,0.04)' }}>
              <h5 style={{ margin: '0 0 12px', color: '#e8a838', fontSize: '0.85rem' }}>Add New Participant (not in system)</h5>
              {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

              {/* Global user search autocomplete */}
              <div className="form-group" style={{ position: 'relative', marginBottom: 14 }}>
                <label style={{ fontSize: '0.75rem' }}>Search existing participants</label>
                <input
                  value={userAC.query}
                  onChange={e => userAC.handleChange(e.target.value)}
                  onFocus={() => userAC.query.length >= 1 && userAC.setShowList(true)}
                  placeholder="Type name, email or phone..."
                  autoComplete="off"
                />
                {userAC.showList && (userAC.results.length > 0 || userAC.loading) && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, zIndex: 50, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                    {userAC.loading && <div style={{ padding: '10px 14px', color: '#8a9bb5', fontSize: '0.8rem' }}>Searching...</div>}
                    {userAC.results.map(u => (
                      <button key={u.id} type="button"
                        onClick={() => selectExistingUser(u)}
                        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #2a3f56', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#2a3f56'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <div>
                          <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{u.first_name} {u.last_name}</div>
                          <div style={{ color: '#8a9bb5', fontSize: '0.72rem' }}>{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                        </div>
                        <span style={{ color: '#4a9eff', fontSize: '0.7rem' }}>Select ↓</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <form onSubmit={handleAdd}>
                <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>First Name <span style={{ color: '#e85d5d' }}>*</span></label>
                    <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Shannon" required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Last Name <span style={{ color: '#e85d5d' }}>*</span></label>
                    <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Smith" required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Email <span style={{ color: '#e85d5d' }}>*</span></label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="shannon@email.com" required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Cell Phone <span style={{ color: '#e85d5d' }}>*</span></label>
                    <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="416-555-0100" required />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Adding...' : 'Add Participant'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowForm(false); userAC.reset(); setForm({ first_name: '', last_name: '', email: '', phone: '' }); }}>Cancel</button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Participants list */}
      {registrations.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>
          No participants yet. Add participants above.
        </div>
      ) : isTeamEvent ? (
        /* ── Team event: group by team ── */
        <div>
          {/* Unassigned first */}
          {registrations.filter(r => !teamByUserId[r.user_id]).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: '#e85d5d', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                <IconWarning size="12px" style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />Not assigned to a team
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[...registrations.filter(r => !teamByUserId[r.user_id])].sort((a, b) => participantSort === 'name'
                  ? `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
                  : (a.participant_number || 0) - (b.participant_number || 0)
                ).map(r => (
                  <ParticipantRow key={r.id} reg={r} teamInfo={null} highlighted={highlightUserId === r.user_id} onViewed={clearHighlight} eventId={eventId} onRefresh={onRefresh} />
                ))}
              </div>
            </div>
          )}
          {/* Team groups */}
          {teams.map(t => {
            const members = registrations.filter(r => teamByUserId[r.user_id]?.team_name === t.team_name);
            if (members.length === 0) return null;
            return (
              <div key={t.id} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  {t.team_number && <span style={{ color: '#e8a838', fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '1rem' }}>#{t.team_number}</span>}
                  <span style={{ color: '#f0f2f5', fontWeight: 700, fontSize: '1rem' }}>{t.team_name}</span>
                  <span style={{ color: '#5c6d83', fontSize: '0.72rem' }}>{members.length} member{members.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[...members].sort((a, b) => participantSort === 'name'
                    ? `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
                    : (a.participant_number || 0) - (b.participant_number || 0)
                  ).map(r => (
                    <ParticipantRow key={r.id} reg={r} teamInfo={teamByUserId[r.user_id]} highlighted={highlightUserId === r.user_id} onViewed={clearHighlight} eventId={eventId} onRefresh={onRefresh} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Individual event: flat list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[...registrations].sort((a, b) => participantSort === 'name'
            ? `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
            : (a.participant_number || 0) - (b.participant_number || 0)
          ).map(r => (
            <ParticipantRow key={r.id} reg={r} teamInfo={null} highlighted={highlightUserId === r.user_id} onViewed={clearHighlight} tournaments={tournaments} eventId={eventId} isIndividualEvent={!isTeamEvent} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function ParticipantRow({ reg, teamInfo, highlighted, onViewed, tournaments, eventId, isIndividualEvent, onRefresh }) {
  const rowRef = useRef(null);
  const [participantTournaments, setParticipantTournaments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: reg.first_name, last_name: reg.last_name, phone: reg.phone || '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (highlighted && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => { if (onViewed) onViewed(); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlighted]); // eslint-disable-line

  function startEdit() {
    setEditing(true);
    setEditError('');
    if (isIndividualEvent && eventId && reg.user_id && tournaments?.length > 0) {
      setLoadingEnrollments(true);
      tournamentApi.getEnrolledParticipants(eventId).then(res => {
        const p = (res.data.data || []).find(pp => pp.id === reg.user_id);
        setParticipantTournaments(p?.tournament_ids || []);
      }).catch(() => {}).finally(() => setLoadingEnrollments(false));
    }
  }

  async function handleTournamentToggle(tournId) {
    setLoadingEnrollments(true);
    const newIds = participantTournaments.includes(tournId)
      ? participantTournaments.filter(id => id !== tournId)
      : [...participantTournaments, tournId];
    try {
      await tournamentApi.updateParticipantTournaments(eventId, reg.user_id, { tournament_ids: newIds });
      setParticipantTournaments(newIds);
    } catch (err) {
      console.error('Failed to update tournament enrollment:', err);
    } finally {
      setLoadingEnrollments(false);
    }
  }

  async function handleEditSave(e) {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      await eventsApi.updateRegistrationDetails(eventId, reg.id, editForm);
      setEditing(false);
      onRefresh();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm(`Remove ${reg.first_name} ${reg.last_name} from this event?`)) return;
    setRemoving(true);
    try {
      await eventsApi.removeRegistration(eventId, reg.id);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove participant');
      setRemoving(false);
    }
  }

  return (
    <div ref={rowRef} className="card" style={{ padding: '16px 20px', border: highlighted ? '1px solid #4a9eff' : undefined, background: highlighted ? '#1a3a2a' : undefined, transition: 'all 0.5s' }}>
      {editing ? (
        <form onSubmit={handleEditSave}>
          <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.75rem' }}>First Name</label>
              <input value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} required />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.75rem' }}>Last Name</label>
              <input value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} required />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.75rem' }}>Phone <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
              <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          {isIndividualEvent && tournaments && tournaments.length > 0 && (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '0.75rem' }}>Tournaments Entered</label>
              {loadingEnrollments ? (
                <div style={{ color: '#8a9bb5', fontSize: '0.8rem' }}>Loading...</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8, marginTop: 6 }}>
                  {tournaments.filter(t => !t.parent_tournament_id).map(t => {
                    const checked = participantTournaments.includes(t.id);
                    return (
                      <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1px solid ${checked ? '#4a9eff' : '#2a3f56'}`, background: checked ? 'rgba(74,158,255,0.08)' : '#111f2e' }}>
                        <input type="checkbox" checked={checked} onChange={() => handleTournamentToggle(t.id)} disabled={loadingEnrollments} style={{ accentColor: '#4a9eff', width: 16, height: 16, flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: checked ? '#f0f2f5' : '#8a9bb5', fontSize: '0.83rem', fontWeight: checked ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                          <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                            {t.is_main_tournament && <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>MAIN</span>}
                            {!t.is_main_tournament && t.tournament_type !== 'calcutta' && <span style={{ background: '#4a9eff', color: '#fff', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>SECONDARY</span>}
                            {t.tournament_type === 'calcutta' && <span style={{ background: '#9b59b6', color: '#fff', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>CALCUTTA</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {editError && <div style={{ color: '#e85d5d', fontSize: '0.8rem', marginBottom: 8 }}>{editError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={editSaving} className="btn btn-primary btn-sm">{editSaving ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(false)} className="btn btn-ghost btn-sm">Cancel</button>
          </div>
        </form>
      ) : (
        <div className="team-card-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {reg.participant_number && <span style={{ background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 6, padding: '2px 8px', fontSize: '0.75rem', color: '#e8a838', fontWeight: 700, fontFamily: 'Oswald, sans-serif' }}>#{reg.participant_number}</span>}
              <span style={{ fontWeight: 600, color: '#f0f2f5', fontSize: '0.88rem' }}>{reg.first_name} {reg.last_name}</span>
              {teamInfo?.is_captain && (
                <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>CAPTAIN</span>
              )}
              {reg.user_role === 'organizer' && (
                <span style={{ background: '#4a9eff', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>ORGANIZER</span>
              )}
            </div>
            <div style={{ color: '#8a9bb5', fontSize: '0.73rem', marginTop: 4 }}>
              {reg.email}{reg.phone ? ` · ${reg.phone}` : ''}
              {teamInfo && (
                <span style={{ marginLeft: 8 }}>
                  {teamInfo.team_number && <span style={{ color: '#e8a838', fontFamily: 'Oswald, sans-serif', fontWeight: 700, marginRight: 4 }}>#{teamInfo.team_number}</span>}
                  {teamInfo.team_name}
                </span>
              )}
            </div>
          </div>
          <div className="team-card-actions" style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
            <button className="btn btn-ghost btn-sm" style={{ color: '#4a9eff' }} onClick={startEdit}>Edit</button>
            <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={handleRemove} disabled={removing}>{removing ? '...' : 'Remove'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TEAMS TAB ──────────��────────────────────────────────────
function TeamsTab({ eventId, event, teams, tournaments, registrations, onRefresh, setActiveTab, setHighlightUserId }) {
  const isIndividualEvent = event?.event_type === 'individual';

  if (isIndividualEvent) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '60px 24px', color: '#8a9bb5' }}>
        <div style={{ marginBottom: 12 }}><IconUser size="32px" /></div>
        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#f0f2f5', marginBottom: 8 }}>Individual Event</div>
        <div style={{ fontSize: '0.85rem' }}>This event is set up for individual participants. Teams cannot be added to individual events.</div>
      </div>
    );
  }
  const [teamSort, setTeamSort] = useState('number'); // 'number' or 'name'

  // Invite URL state
  const inviteUrl = `${window.location.origin}/public/events/${eventId}`;
  const [linkCopied, setLinkCopied] = useState(false);
  function copyInviteLink() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(inviteUrl).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      });
    } else {
      const el = document.createElement('textarea');
      el.value = inviteUrl;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }

  // Pending registrations state
  const [pendingOpen, setPendingOpen] = useState(true);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingError, setPendingError] = useState('');
  // Per-registration team/role selection: { [regId]: { teamId, role } }
  const [pendingSelections, setPendingSelections] = useState({});

  useEffect(() => {
    setPendingLoading(true);
    eventsApi.getRegistrations(eventId)
      .then(res => setPendingRegs((res.data.data || []).filter(r => r.registration_status === 'pending')))
      .catch(() => {})
      .finally(() => setPendingLoading(false));
  }, [eventId, teams]);

  function setPendingTeam(regId, teamId) {
    setPendingSelections(prev => ({ ...prev, [regId]: { ...(prev[regId] || {}), teamId } }));
  }
  function setPendingRole(regId, role) {
    setPendingSelections(prev => ({ ...prev, [regId]: { ...(prev[regId] || {}), role } }));
  }

  async function handlePendingAction(regId, status) {
    const sel = pendingSelections[regId] || {};
    // For team events, require a team selection before approving
    if (status === 'confirmed' && !sel.teamId) {
      setPendingError('Please select a team for this participant before approving.');
      return;
    }
    setPendingAction(regId);
    setPendingError('');
    try {
      await eventsApi.updateRegistrationStatus(eventId, regId, status, sel.teamId, sel.role || 'member');
      setPendingRegs(prev => prev.filter(r => r.id !== regId));
      setPendingSelections(prev => { const n = { ...prev }; delete n[regId]; return n; });
      if (status === 'confirmed') onRefresh();
    } catch (err) {
      setPendingError(err.response?.data?.message || 'Action failed');
    } finally {
      setPendingAction(null);
    }
  }

  // Admin panel state
  const [adminStatus, setAdminStatus] = useState(null);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [adminActionError, setAdminActionError] = useState('');
  const [adminActionSuccess, setAdminActionSuccess] = useState('');
  const [assignPickerTeam, setAssignPickerTeam] = useState(null); // { id, team_name }
  const [assignPickerSearch, setAssignPickerSearch] = useState('');
  const [assignConfirmUser, setAssignConfirmUser] = useState(null); // user object
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignCaptainMode, setAssignCaptainMode] = useState('search'); // 'search' | 'new'
  const [assignNewCaptainName, setAssignNewCaptainName] = useState('');
  const [assignNewCaptainEmail, setAssignNewCaptainEmail] = useState('');
  const [approveLoading, setApproveLoading] = useState(null); // requestId
  const [denyLoading, setDenyLoading] = useState(null); // requestId
  const [makeCaptainChecked, setMakeCaptainChecked] = useState({}); // { [requestId]: bool }

  useEffect(() => {
    if (!eventId) return;
    teamAdminApi.getStatus(eventId)
      .then(r => setAdminStatus(r.data))
      .catch(() => {});
  }, [eventId]);

  async function refetchAdminStatus() {
    try {
      const r = await teamAdminApi.getStatus(eventId);
      setAdminStatus(r.data);
    } catch (_) {}
  }

  async function handleApproveRequest(requestId, makeCaptain) {
    setApproveLoading(requestId);
    setAdminActionError('');
    try {
      await teamAdminApi.approveRequest(eventId, requestId, makeCaptain);
      setAdminActionSuccess('Request approved.');
      await refetchAdminStatus();
    } catch (err) {
      setAdminActionError(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setApproveLoading(null);
    }
  }

  async function handleDenyRequest(requestId) {
    setDenyLoading(requestId);
    setAdminActionError('');
    try {
      await teamAdminApi.denyRequest(eventId, requestId, '');
      setAdminActionSuccess('Request denied.');
      await refetchAdminStatus();
    } catch (err) {
      setAdminActionError(err.response?.data?.error || 'Failed to deny request');
    } finally {
      setDenyLoading(null);
    }
  }

  async function handleAssignCaptain(teamId, userId) {
    setAssignLoading(true);
    setAdminActionError('');
    try {
      await teamAdminApi.assignCaptain(eventId, teamId, userId);
      const userName = assignConfirmUser ? `${assignConfirmUser.first_name} ${assignConfirmUser.last_name}` : 'User';
      setAdminActionSuccess(`Captain assigned. Email sent to ${userName}.`);
      setAssignPickerTeam(null);
      setAssignConfirmUser(null);
      setAssignPickerSearch('');
      setAssignCaptainMode('search');
      await refetchAdminStatus();
      onRefresh();
    } catch (err) {
      setAdminActionError(err.response?.data?.error || 'Failed to assign captain');
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleAssignNewCaptain(teamId) {
    if (!assignNewCaptainName.trim() || !assignNewCaptainEmail.trim()) {
      setAdminActionError('Name and email are required');
      return;
    }
    setAssignLoading(true);
    setAdminActionError('');
    try {
      await teamAdminApi.assignNewCaptain(eventId, teamId, assignNewCaptainName.trim(), assignNewCaptainEmail.trim());
      setAdminActionSuccess(`New captain assigned. Registration and invitation emails sent to ${assignNewCaptainEmail.trim()}.`);
      setAssignPickerTeam(null);
      setAssignNewCaptainName('');
      setAssignNewCaptainEmail('');
      setAssignCaptainMode('search');
      await refetchAdminStatus();
      onRefresh();
    } catch (err) {
      setAdminActionError(err.response?.data?.error || 'Failed to assign captain');
    } finally {
      setAssignLoading(false);
    }
  }

  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [allTeams, setAllTeams]       = useState([]);
  const [searchQ, setSearchQ]         = useState('');
  const [selected, setSelected]       = useState(new Set());
  const [bulkTournamentIds, setBulkTournamentIds] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState('');
  const [successMsg, setSuccessMsg]     = useState('');

  // Manual add team form state
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualForm, setManualForm]       = useState({ team_name: '', captain_name: '', captain_user_id: '' });
  const [manualTournamentIds, setManualTournamentIds] = useState([]);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const manualCaptainAC = useAutocomplete(q => searchApi.searchUsers(q));
  const [manualCaptainMode, setManualCaptainMode] = useState('search'); // 'search' | 'new'
  const [manualNewCaptainName, setManualNewCaptainName] = useState('');
  const [manualNewCaptainEmail, setManualNewCaptainEmail] = useState('');

  const [editingTeam, setEditingTeam] = useState(null);
  const [editForm,    setEditForm]    = useState({});

  const editCaptainAC = useAutocomplete(q => searchApi.searchUsers(q));
  const [editCaptainMode, setEditCaptainMode] = useState('search'); // 'search' | 'new'
  const [editNewCaptainName, setEditNewCaptainName] = useState('');
  const [editNewCaptainEmail, setEditNewCaptainEmail] = useState('');

  const enrollableTournaments = tournaments.filter(t => !t.parent_tournament_id);

  function toggleTournament(ids, tid) {
    return ids.includes(tid) ? ids.filter(x => x !== tid) : [...ids, tid];
  }

  // Fetch all system teams when bulk-add panel opens
  useEffect(() => {
    if (!showBulkAdd) return;
    setLoadingTeams(true);
    searchApi.getAllTeams(eventId, '')
      .then(res => setAllTeams(res.data.data || []))
      .catch(() => setAllTeams([]))
      .finally(() => setLoadingTeams(false));
  }, [showBulkAdd, eventId]);

  // Filter teams by search
  const searchTimer = useRef(null);
  function handleSearch(val) {
    setSearchQ(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setLoadingTeams(true);
      searchApi.getAllTeams(eventId, val)
        .then(res => setAllTeams(res.data.data || []))
        .catch(() => setAllTeams([]))
        .finally(() => setLoadingTeams(false));
    }, 300);
  }

  function toggleSelect(teamName) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(teamName)) next.delete(teamName); else next.add(teamName);
      return next;
    });
  }

  function selectAll() {
    const available = allTeams.filter(t => !t.already_in_event);
    if (selected.size === available.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(available.map(t => t.team_name)));
    }
  }

  async function handleBulkAdd() {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError('');
    setSuccessMsg('');
    try {
      const teamsToAdd = allTeams
        .filter(t => selected.has(t.team_name) && !t.already_in_event)
        .map(t => ({ team_name: t.team_name, captain_name: t.captain_name || undefined, captain_user_id: t.captain_user_id || undefined, source_team_id: t.id || undefined, system_number: t.system_number || undefined }));
      const res = await tournamentApi.bulkAddTeams(eventId, { teams: teamsToAdd, tournament_ids: bulkTournamentIds });
      const { created, skipped } = res.data.data;
      let msg = `${created.length} team(s) added.`;
      if (skipped.length > 0) msg += ` ${skipped.length} skipped (already exist).`;
      setSuccessMsg(msg);
      setSelected(new Set());
      onRefresh();
      // Re-fetch teams to update already_in_event flags
      searchApi.getAllTeams(eventId, searchQ)
        .then(r => setAllTeams(r.data.data || []))
        .catch(() => {});
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add teams');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleManualAdd() {
    if (!manualForm.team_name.trim()) return;
    if (manualCaptainMode === 'new' && manualNewCaptainEmail && !manualNewCaptainName.trim()) {
      setError('Captain name is required when adding a new participant');
      return;
    }
    setManualSubmitting(true);
    setError('');
    setSuccessMsg('');
    try {
      const payload = {
        team_name: manualForm.team_name.trim(),
        tournament_ids: manualTournamentIds
      };
      if (manualCaptainMode === 'search') {
        payload.captain_name = manualForm.captain_name || undefined;
        payload.captain_user_id = manualForm.captain_user_id || undefined;
      } else {
        payload.captain_new_name = manualNewCaptainName.trim() || undefined;
        payload.captain_new_email = manualNewCaptainEmail.trim() || undefined;
      }
      await tournamentApi.createTeam(eventId, payload);
      setSuccessMsg(`Team "${manualForm.team_name.trim()}" added.`);
      setManualForm({ team_name: '', captain_name: '', captain_user_id: '' });
      setManualTournamentIds([]);
      manualCaptainAC.reset();
      setManualCaptainMode('search');
      setManualNewCaptainName('');
      setManualNewCaptainEmail('');
      setShowManualAdd(false);
      onRefresh();
      // Re-fetch system teams to update the list
      if (showBulkAdd) {
        searchApi.getAllTeams(eventId, searchQ)
          .then(r => setAllTeams(r.data.data || []))
          .catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add team');
    } finally {
      setManualSubmitting(false);
    }
  }

  async function handleSaveEdit(teamId) {
    try {
      const payload = {
        team_name: editForm.team_name,
        team_number: editForm.team_number ? parseInt(editForm.team_number) : null,
      };
      if (editCaptainMode === 'search') {
        payload.captain_user_id = editForm.captain_user_id || null;
        payload.captain_name = editForm.captain_name || null;
      } else {
        payload.captain_new_name = editNewCaptainName.trim() || undefined;
        payload.captain_new_email = editNewCaptainEmail.trim() || undefined;
      }
      await tournamentApi.updateTeam(eventId, teamId, payload);
      await tournamentApi.updateTeamTournaments(eventId, teamId, { tournament_ids: editForm.tournament_ids });
      setEditingTeam(null);
      editCaptainAC.reset();
      setEditCaptainMode('search');
      setEditNewCaptainName('');
      setEditNewCaptainEmail('');
      onRefresh();
    } catch (err) { alert(err.response?.data?.message || 'Failed to save'); }
  }

  async function handleRemove(teamId, teamName) {
    if (!window.confirm(`Remove team "${teamName}" from this event?`)) return;
    try { await tournamentApi.removeTeam(eventId, teamId); onRefresh(); }
    catch (err) { alert('Failed to remove team'); }
  }

  const TournamentCheckboxes = ({ selectedIds, onChange }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
      {enrollableTournaments.length === 0 ? (
        <div style={{ color: '#5c6d83', fontSize: '0.8rem' }}>No tournaments created yet</div>
      ) : enrollableTournaments.map(t => {
        const checked = selectedIds.includes(t.id);
        return (
          <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1px solid ${checked ? '#4a9eff' : '#2a3f56'}`, background: checked ? 'rgba(74,158,255,0.08)' : '#111f2e' }}>
            <input type="checkbox" checked={checked} onChange={() => onChange(toggleTournament(selectedIds, t.id))} style={{ accentColor: '#4a9eff', width: 16, height: 16, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: checked ? '#f0f2f5' : '#8a9bb5', fontSize: '0.83rem', fontWeight: checked ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                {t.is_main_tournament && <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>MAIN</span>}
                {!t.is_main_tournament && t.tournament_type !== 'calcutta' && <span style={{ background: '#4a9eff', color: '#fff', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>SECONDARY</span>}
                {t.tournament_type === 'calcutta' && <span style={{ background: '#9b59b6', color: '#fff', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>CALCUTTA</span>}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );

  const availableTeams = allTeams.filter(t => !t.already_in_event);
  const alreadyInEvent = allTeams.filter(t => t.already_in_event);

  // First orphaned team per team id (for auto-checking "make captain" on first pending request)
  const firstRequestPerTeam = {};
  if (adminStatus?.pending_requests) {
    for (const req of adminStatus.pending_requests) {
      if (!firstRequestPerTeam[req.team_id]) firstRequestPerTeam[req.team_id] = req.request_id;
    }
  }

  const filteredPickerUsers = (adminStatus?.unassigned_participants || []).filter(u => {
    if (!assignPickerSearch) return true;
    const q = assignPickerSearch.toLowerCase();
    return (u.first_name + ' ' + u.last_name).toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  return (
    <div>
      {/* ── Invite Link ─────────────────────────────────────── */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ color: '#8a9bb5', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Team Invite Link</div>
        <div style={{ fontSize: '0.78rem', color: '#8a9bb5', marginBottom: 12 }}>
          Share this link with participants so they can sign up and register for this event.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0, background: 'rgba(0,0,0,0.2)', border: '1px solid #2a3f56', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', color: '#8a9bb5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {inviteUrl}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={copyInviteLink} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {linkCopied ? '✓ Copied!' : '⧉ Copy Link'}
          </button>
        </div>
      </div>

      {/* ── Pending Registrations ───────────────────────────── */}
      {(pendingLoading || pendingRegs.length > 0) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <button
            onClick={() => setPendingOpen(o => !o)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 20px', color: '#f0f2f5' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Pending Registration Requests</span>
              {pendingRegs.length > 0 && (
                <span style={{ background: '#e8a838', color: '#000', borderRadius: 12, padding: '1px 9px', fontSize: '0.72rem', fontWeight: 700 }}>{pendingRegs.length}</span>
              )}
            </div>
            <span style={{ color: '#5c6d83', fontSize: '0.8rem' }}>{pendingOpen ? '▲ Hide' : '▼ Show'}</span>
          </button>

          {pendingOpen && (
            <div style={{ padding: '0 20px 16px' }}>
              {pendingError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{pendingError}</div>}
              {pendingLoading ? (
                <div className="spinner" />
              ) : pendingRegs.length === 0 ? (
                <div style={{ color: '#5c6d83', fontSize: '0.82rem', textAlign: 'center', padding: '16px 0' }}>No pending requests.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pendingRegs.map(r => {
                    const sel = pendingSelections[r.id] || {};
                    return (
                    <div key={r.id} style={{ padding: '12px 14px', background: 'rgba(0,0,0,0.15)', borderRadius: 8, border: '1px solid #2a3f56' }}>
                      {/* Participant info */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: 600, color: '#f0f2f5', fontSize: '0.88rem' }}>{r.first_name} {r.last_name}</div>
                        <div style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>{r.email}</div>
                        <div style={{ color: '#5c6d83', fontSize: '0.72rem', marginTop: 2 }}>
                          Requested {new Date(r.created_at).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Team assignment (required for team events) */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                        <select
                          value={sel.teamId || ''}
                          onChange={e => setPendingTeam(r.id, e.target.value)}
                          style={{ flex: 1, minWidth: 160, fontSize: '0.82rem', padding: '6px 10px', background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 6, color: sel.teamId ? '#f0f2f5' : '#5c6d83' }}
                        >
                          <option value="">— Select team —</option>
                          {teams.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.team_number ? `#${t.team_number} ` : ''}{t.team_name}
                            </option>
                          ))}
                        </select>
                        <select
                          value={sel.role || 'member'}
                          onChange={e => setPendingRole(r.id, e.target.value)}
                          style={{ fontSize: '0.82rem', padding: '6px 10px', background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 6, color: '#f0f2f5' }}
                        >
                          <option value="member">Member</option>
                          <option value="captain">Captain</option>
                        </select>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handlePendingAction(r.id, 'confirmed')}
                          disabled={pendingAction === r.id || !sel.teamId}
                          title={!sel.teamId ? 'Select a team first' : ''}
                        >
                          {pendingAction === r.id ? '...' : 'Approve & Add to Team'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handlePendingAction(r.id, 'cancelled')}
                          disabled={pendingAction === r.id}
                          style={{ color: '#e85d5d' }}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Admin Warning Card ─── */}
      {adminStatus && (adminStatus.orphaned_teams.length > 0 || adminStatus.pending_requests.length > 0 || adminStatus.unassigned_participants.length > 0) && (
        <div style={{
          background: '#fff8e6',
          border: '2px solid #e8a838',
          borderRadius: 12,
          padding: '16px 20px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16
        }}>
          <div>
            <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 700, color: '#0d1b2a', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
              ⚠ Team Setup Issues
            </div>
            <div style={{ fontSize: 14, color: '#444', lineHeight: 1.5 }}>
              {adminStatus.orphaned_teams.length > 0 && <div>{adminStatus.orphaned_teams.length} teams need captains</div>}
              {adminStatus.pending_requests.length > 0 && <div>{adminStatus.pending_requests.length} pending join requests</div>}
              {adminStatus.unassigned_participants.length > 0 && <div>{adminStatus.unassigned_participants.length} participants without a team</div>}
            </div>
          </div>
          <button
            onClick={() => { setAdminPanelOpen(true); setAdminActionError(''); setAdminActionSuccess(''); }}
            style={{ background: '#0d1b2a', color: '#e8a838', border: 'none', padding: '12px 22px', borderRadius: 8, fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Review &amp; Approve
          </button>
        </div>
      )}

      {/* ─── Admin Panel Modal ─── */}
      {adminPanelOpen && adminStatus && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }} onClick={e => { if (e.target === e.currentTarget) setAdminPanelOpen(false); }}>
          <div style={{ background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 16, width: '100%', maxWidth: 800, padding: '28px 32px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 700, color: '#f0f2f5', letterSpacing: 1 }}>Team Admin Panel</div>
              <button onClick={() => setAdminPanelOpen(false)} style={{ background: 'none', border: 'none', color: '#8a9bb5', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {adminActionSuccess && <div style={{ background: 'rgba(46,204,113,0.1)', border: '1px solid #2ecc71', color: '#2ecc71', padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem', marginBottom: 16 }}>{adminActionSuccess}</div>}
            {adminActionError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{adminActionError}</div>}

            {/* Section 1: Orphaned Teams */}
            {adminStatus.orphaned_teams.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: '#e8a838', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
                  Teams Needing Captains ({adminStatus.orphaned_teams.length})
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ color: '#8a9bb5', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #2a3f56' }}>Team Name</th>
                        <th style={{ textAlign: 'center', padding: '8px 12px', borderBottom: '1px solid #2a3f56' }}>#</th>
                        <th style={{ textAlign: 'center', padding: '8px 12px', borderBottom: '1px solid #2a3f56' }}>Members</th>
                        <th style={{ textAlign: 'center', padding: '8px 12px', borderBottom: '1px solid #2a3f56' }}>Pending</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid #2a3f56' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminStatus.orphaned_teams.map(t => (
                        <React.Fragment key={t.id}>
                          <tr style={{ borderBottom: assignPickerTeam && assignPickerTeam.id === t.id ? 'none' : '1px solid #1a2d3f' }}>
                            <td style={{ padding: '10px 12px', color: '#f0f2f5', fontWeight: 500 }}>{t.team_name}</td>
                            <td style={{ padding: '10px 12px', color: '#8a9bb5', textAlign: 'center' }}>{t.team_number || '—'}</td>
                            <td style={{ padding: '10px 12px', color: '#8a9bb5', textAlign: 'center' }}>{t.member_count}/{t.max_members}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <span style={{ color: t.pending_request_count > 0 ? '#e8a838' : '#8a9bb5' }}>{t.pending_request_count}</span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              {t.pending_request_count > 0 ? (
                                <button onClick={() => { }} style={{ background: 'rgba(74,158,255,0.1)', color: '#4a9eff', border: '1px solid #4a9eff', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                                  Manage Requests
                                </button>
                              ) : assignPickerTeam && assignPickerTeam.id === t.id ? (
                                <button onClick={() => { setAssignPickerTeam(null); setAssignConfirmUser(null); setAssignPickerSearch(''); }} style={{ background: 'rgba(232,168,56,0.25)', color: '#e8a838', border: '1px solid #e8a838', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                                  Cancel
                                </button>
                              ) : (
                                <button onClick={() => { setAssignPickerTeam(t); setAssignPickerSearch(''); setAssignConfirmUser(null); setAdminActionError(''); setAssignCaptainMode('search'); setAssignNewCaptainName(''); setAssignNewCaptainEmail(''); }} style={{ background: 'rgba(232,168,56,0.1)', color: '#e8a838', border: '1px solid #e8a838', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                                  Assign Captain
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* Inline Assign Captain Picker — renders directly below the selected team */}
                          {assignPickerTeam && assignPickerTeam.id === t.id && (
                            <tr>
                              <td colSpan={5} style={{ padding: 0, borderBottom: '1px solid #1a2d3f' }}>
                                <div style={{ background: '#162231', border: '1px solid #2a3f56', borderRadius: '0 0 12px 12px', padding: '16px 20px', margin: '0 4px 8px 4px' }}>
                                  <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, color: '#f0f2f5', marginBottom: 12 }}>Assign Captain to {assignPickerTeam.team_name}</div>
                                  {assignConfirmUser ? (
                                    <div>
                                      <div style={{ color: '#f0f2f5', fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
                                        Assign <strong>{assignConfirmUser.first_name} {assignConfirmUser.last_name}</strong> as captain of <strong>{assignPickerTeam.team_name}</strong>? They'll receive an email notification and can immediately start inviting teammates.
                                      </div>
                                      <div style={{ display: 'flex', gap: 10 }}>
                                        <button onClick={() => setAssignConfirmUser(null)} style={{ background: 'transparent', border: '1px solid #2a3f56', color: '#8a9bb5', padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                                        <button onClick={() => handleAssignCaptain(assignPickerTeam.id, assignConfirmUser.user_id)} disabled={assignLoading} style={{ background: '#e8a838', color: '#0d1b2a', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: assignLoading ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', opacity: assignLoading ? 0.7 : 1 }}>
                                          {assignLoading ? 'Assigning...' : 'Confirm — Assign Captain'}
                                        </button>
                                      </div>
                                      {adminActionError && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 10 }}>{adminActionError}</div>}
                                    </div>
                                  ) : (
                                    <div>
                                      <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderRadius: 6, overflow: 'hidden', border: '1px solid #2a3f56', width: 'fit-content' }}>
                                        <button type="button"
                                          style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: assignCaptainMode === 'search' ? '#4a9eff' : '#1e2d3d', color: assignCaptainMode === 'search' ? '#fff' : '#8a9bb5' }}
                                          onClick={() => setAssignCaptainMode('search')}>Search Existing</button>
                                        <button type="button"
                                          style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: assignCaptainMode === 'new' ? '#e8a838' : '#1e2d3d', color: assignCaptainMode === 'new' ? '#0d1b2a' : '#8a9bb5' }}
                                          onClick={() => setAssignCaptainMode('new')}>Add New Participant</button>
                                      </div>
                                      {assignCaptainMode === 'search' ? (
                                        <div>
                                          <input
                                            value={assignPickerSearch}
                                            onChange={e => setAssignPickerSearch(e.target.value)}
                                            placeholder="Search by name or email..."
                                            style={{ width: '100%', marginBottom: 12, background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 6, padding: '8px 12px', color: '#f0f2f5', fontSize: 14 }}
                                          />
                                          {filteredPickerUsers.length === 0 ? (
                                            <div style={{ color: '#8a9bb5', fontSize: 13, padding: '8px 0' }}>
                                              No unassigned participants available. All registrants are already on a team.
                                            </div>
                                          ) : (
                                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                              {filteredPickerUsers.map(u => (
                                                <div key={u.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #1a2d3f' }}>
                                                  <div>
                                                    <div style={{ color: '#f0f2f5', fontWeight: 500, fontSize: 13 }}>{u.first_name} {u.last_name}</div>
                                                    <div style={{ color: '#8a9bb5', fontSize: 11 }}>{u.email}</div>
                                                  </div>
                                                  <button onClick={() => setAssignConfirmUser(u)} style={{ background: 'rgba(232,168,56,0.1)', color: '#e8a838', border: '1px solid #e8a838', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                                                    Make Captain
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div>
                                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                            <input
                                              type="text"
                                              placeholder="Full name *"
                                              value={assignNewCaptainName}
                                              onChange={e => setAssignNewCaptainName(e.target.value)}
                                              style={{ background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 6, padding: '8px 12px', color: '#f0f2f5', fontSize: 14 }}
                                            />
                                            <input
                                              type="email"
                                              placeholder="Email address *"
                                              value={assignNewCaptainEmail}
                                              onChange={e => setAssignNewCaptainEmail(e.target.value)}
                                              style={{ background: '#0d1b2a', border: '1px solid #2a3f56', borderRadius: 6, padding: '8px 12px', color: '#f0f2f5', fontSize: 14 }}
                                            />
                                          </div>
                                          <div style={{ fontSize: '0.72rem', color: '#8a9bb5', marginBottom: 10 }}>
                                            Registration Confirmed and Team Invitation emails will be sent to this address.
                                          </div>
                                          <button
                                            onClick={() => handleAssignNewCaptain(assignPickerTeam.id)}
                                            disabled={assignLoading || !assignNewCaptainName.trim() || !assignNewCaptainEmail.trim()}
                                            style={{ background: '#e8a838', color: '#0d1b2a', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: assignLoading ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', opacity: (assignLoading || !assignNewCaptainName.trim() || !assignNewCaptainEmail.trim()) ? 0.7 : 1 }}
                                          >
                                            {assignLoading ? 'Assigning...' : 'Assign New Captain'}
                                          </button>
                                          {adminActionError && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 10 }}>{adminActionError}</div>}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section 2: Pending Join Requests */}
            {adminStatus.pending_requests.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: '#4a9eff', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
                  Pending Join Requests ({adminStatus.pending_requests.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {adminStatus.pending_requests.map((req, idx) => {
                    const isFirstForTeam = !req.team_has_captain && firstRequestPerTeam[req.team_id] === req.request_id;
                    const isMakeCaptain = makeCaptainChecked[req.request_id] !== undefined ? makeCaptainChecked[req.request_id] : isFirstForTeam;
                    const isFull = req.team_member_count >= req.team_max_members;
                    return (
                      <div key={req.request_id} style={{ background: '#162231', border: '1px solid #2a3f56', borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: '#f0f2f5', fontSize: 14 }}>{req.user_first_name} {req.user_last_name}</div>
                            <div style={{ color: '#8a9bb5', fontSize: 12 }}>{req.user_email}</div>
                            <div style={{ marginTop: 4, fontSize: 12 }}>
                              <span style={{ color: '#f0f2f5' }}>{req.team_name}</span>
                              <span style={{ color: '#8a9bb5' }}> · {req.team_member_count}/{req.team_max_members} members</span>
                              {!req.team_has_captain && <span style={{ color: '#e8a838', marginLeft: 8 }}>🔴 No captain</span>}
                            </div>
                            {req.message && <div style={{ color: '#8a9bb5', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>"{req.message}"</div>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {!req.team_has_captain && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#8a9bb5', fontSize: 12, cursor: 'pointer' }}>
                                <input type="checkbox" checked={isMakeCaptain} onChange={e => setMakeCaptainChecked(prev => ({ ...prev, [req.request_id]: e.target.checked }))} style={{ accentColor: '#e8a838' }} />
                                Make captain
                              </label>
                            )}
                            <button
                              onClick={() => handleApproveRequest(req.request_id, isMakeCaptain)}
                              disabled={isFull || approveLoading === req.request_id}
                              title={isFull ? 'Team full' : ''}
                              style={{ background: isFull ? 'transparent' : 'rgba(46,204,113,0.15)', color: isFull ? '#8a9bb5' : '#2ecc71', border: `1px solid ${isFull ? '#2a3f56' : '#2ecc71'}`, padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: isFull ? 'not-allowed' : 'pointer', opacity: approveLoading === req.request_id ? 0.6 : 1 }}
                            >
                              {approveLoading === req.request_id ? '...' : isFull ? 'Team full' : '✓ Approve'}
                            </button>
                            <button
                              onClick={() => handleDenyRequest(req.request_id)}
                              disabled={denyLoading === req.request_id}
                              style={{ background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', opacity: denyLoading === req.request_id ? 0.6 : 1 }}
                            >
                              {denyLoading === req.request_id ? '...' : '✗ Deny'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section 3: Unassigned Participants */}
            {adminStatus.unassigned_participants.length > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: '#8a9bb5', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Unassigned Participants ({adminStatus.unassigned_participants.length})
                  </div>
                  <button
                    onClick={() => teamAdminApi.notifyUnassigned(eventId).then(() => setAdminActionSuccess('Reminder emails sent.')).catch(err => setAdminActionError(err.response?.data?.error || 'Failed to send reminders'))}
                    style={{ background: 'transparent', color: '#8a9bb5', border: '1px solid #2a3f56', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                  >
                    Send Reminder to All
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {adminStatus.unassigned_participants.map(p => (
                    <div key={p.user_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#162231', borderRadius: 8, fontSize: 13 }}>
                      <span style={{ color: '#f0f2f5' }}>{p.first_name} {p.last_name}</span>
                      <span style={{ color: '#8a9bb5' }}>{p.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>
            {teams.length} team{teams.length !== 1 ? 's' : ''}
          </span>
          {teams.length > 1 && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-ghost btn-sm" style={{ padding: '4px 10px', fontSize: '0.72rem', color: teamSort === 'number' ? '#e8a838' : '#5c6d83', background: teamSort === 'number' ? 'rgba(232,168,56,0.1)' : 'transparent' }} onClick={() => setTeamSort('number')}>#</button>
              <button className="btn btn-ghost btn-sm" style={{ padding: '4px 10px', fontSize: '0.72rem', color: teamSort === 'name' ? '#e8a838' : '#5c6d83', background: teamSort === 'name' ? 'rgba(232,168,56,0.1)' : 'transparent' }} onClick={() => setTeamSort('name')}>A–Z</button>
            </div>
          )}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowBulkAdd(!showBulkAdd); setError(''); setSuccessMsg(''); }}>
          {showBulkAdd ? 'Cancel' : '+ Add Teams'}
        </button>
      </div>

      {showBulkAdd && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 4 }}>Add Teams from System</h4>
          <p style={{ color: '#8a9bb5', fontSize: '0.8rem', marginBottom: 16 }}>Select teams to add to this event and choose which tournaments to enroll them in.</p>

          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
          {successMsg && <div className="alert alert-success" style={{ marginBottom: 12, background: 'rgba(46,204,113,0.1)', border: '1px solid #2ecc71', color: '#2ecc71', padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem' }}>{successMsg}</div>}

          {/* Search */}
          <div className="form-group" style={{ marginBottom: 12 }}>
            <input
              value={searchQ}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search teams by name..."
              autoComplete="off"
              style={{ width: '100%' }}
            />
          </div>

          {/* Select all */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#8a9bb5', fontSize: '0.82rem' }}>
              <input
                type="checkbox"
                checked={availableTeams.length > 0 && selected.size === availableTeams.length}
                onChange={selectAll}
                style={{ accentColor: '#4a9eff', width: 16, height: 16 }}
              />
              Select all available ({availableTeams.length})
            </label>
            {selected.size > 0 && (
              <span style={{ color: '#4a9eff', fontSize: '0.82rem', fontWeight: 600 }}>{selected.size} selected</span>
            )}
          </div>

          {/* Team list */}
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #2a3f56', borderRadius: 8, marginBottom: 16 }}>
            {loadingTeams ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#8a9bb5' }}>Loading teams...</div>
            ) : allTeams.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#8a9bb5' }}>No teams found in system{searchQ ? ` matching "${searchQ}"` : ''}.</div>
            ) : (
              <>
                {availableTeams.map(t => {
                  const isSelected = selected.has(t.team_name);
                  return (
                    <label key={t.team_name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1e2d3d', background: isSelected ? 'rgba(74,158,255,0.06)' : 'transparent' }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#1a2a3a'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(t.team_name)} style={{ accentColor: '#4a9eff', width: 16, height: 16, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{t.team_name}</span>
                        {t.captain_name && <span style={{ color: '#8a9bb5', fontSize: '0.72rem', marginLeft: 8 }}>Capt. {t.captain_name}</span>}
                      </div>
                      <span style={{ color: '#5c6d83', fontSize: '0.7rem', flexShrink: 0 }}>{t.times_entered} prev. event{t.times_entered !== '1' ? 's' : ''}</span>
                    </label>
                  );
                })}
                {alreadyInEvent.length > 0 && (
                  <>
                    <div style={{ padding: '8px 14px', background: '#111f2e', color: '#5c6d83', fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid #1e2d3d' }}>Already in this event</div>
                    {alreadyInEvent.map(t => (
                      <div key={t.team_name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #1e2d3d', opacity: 0.5 }}>
                        <input type="checkbox" disabled checked style={{ width: 16, height: 16, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>{t.team_name}</span>
                          {t.captain_name && <span style={{ color: '#5c6d83', fontSize: '0.72rem', marginLeft: 8 }}>Capt. {t.captain_name}</span>}
                        </div>
                        <span style={{ color: '#5c6d83', fontSize: '0.7rem', flexShrink: 0 }}>already added</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* Tournament enrollment for bulk add */}
          {selected.size > 0 && (
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Enroll selected teams in tournaments</label>
              <TournamentCheckboxes selectedIds={bulkTournamentIds} onChange={setBulkTournamentIds} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={handleBulkAdd} disabled={selected.size === 0 || submitting}>
              {submitting ? 'Adding...' : `Add ${selected.size} Team${selected.size !== 1 ? 's' : ''} to Event`}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowBulkAdd(false); setSelected(new Set()); setBulkTournamentIds([]); setSearchQ(''); setShowManualAdd(false); manualCaptainAC.reset(); setManualCaptainMode('search'); setManualNewCaptainName(''); setManualNewCaptainEmail(''); }}>Cancel</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" style={{ color: '#e8a838' }} onClick={() => setShowManualAdd(!showManualAdd)}>
              {showManualAdd ? 'Hide' : '+ Add Team'}
            </button>
          </div>

          {/* Manual add team form */}
          {showManualAdd && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid #e8a838', borderRadius: 8, background: 'rgba(232,168,56,0.04)' }}>
              <h5 style={{ margin: '0 0 12px', color: '#e8a838', fontSize: '0.85rem' }}>Add New Team (not in system)</h5>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.75rem' }}>Team Name *</label>
                <input
                  value={manualForm.team_name}
                  onChange={e => setManualForm(f => ({ ...f, team_name: e.target.value }))}
                  placeholder="Enter team name..."
                  autoComplete="off"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.75rem' }}>Captain <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
                <div style={{ display: 'flex', gap: 0, marginBottom: 8, borderRadius: 6, overflow: 'hidden', border: '1px solid #2a3f56', width: 'fit-content' }}>
                  <button type="button"
                    style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: manualCaptainMode === 'search' ? '#4a9eff' : '#1e2d3d', color: manualCaptainMode === 'search' ? '#fff' : '#8a9bb5' }}
                    onClick={() => setManualCaptainMode('search')}>Search Existing</button>
                  <button type="button"
                    style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: manualCaptainMode === 'new' ? '#e8a838' : '#1e2d3d', color: manualCaptainMode === 'new' ? '#0d1b2a' : '#8a9bb5' }}
                    onClick={() => setManualCaptainMode('new')}>Add New Participant</button>
                </div>
                {manualCaptainMode === 'search' ? (
                  <div style={{ position: 'relative' }}>
                    <input
                      value={manualCaptainAC.query}
                      onChange={e => manualCaptainAC.handleChange(e.target.value)}
                      onFocus={() => manualCaptainAC.query.length >= 1 && manualCaptainAC.setShowList(true)}
                      placeholder="Type name, email or phone..."
                      autoComplete="off"
                    />
                    {manualCaptainAC.showList && (manualCaptainAC.results.length > 0 || manualCaptainAC.loading) && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, zIndex: 50, maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                        {manualCaptainAC.loading && <div style={{ padding: '10px 14px', color: '#8a9bb5', fontSize: '0.8rem' }}>Searching...</div>}
                        {manualCaptainAC.results.map(u => (
                          <button key={u.id} type="button"
                            onClick={() => { setManualForm(f => ({ ...f, captain_user_id: u.id, captain_name: `${u.first_name} ${u.last_name}` })); manualCaptainAC.setQuery(`${u.first_name} ${u.last_name}`); manualCaptainAC.setShowList(false); }}
                            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #2a3f56', display: 'flex', justifyContent: 'space-between' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#2a3f56'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          >
                            <div>
                              <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{u.first_name} {u.last_name}</div>
                              <div style={{ color: '#8a9bb5', fontSize: '0.72rem' }}>{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                            </div>
                            <span style={{ color: '#e8a838', fontSize: '0.7rem', alignSelf: 'center' }}>Set Captain</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {manualForm.captain_name && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>CAPTAIN</span>
                        <span style={{ color: '#f0f2f5', fontSize: '0.82rem' }}>{manualForm.captain_name}</span>
                        <button type="button" style={{ background: 'none', border: 'none', color: '#5c6d83', cursor: 'pointer', fontSize: '0.8rem' }} onClick={() => { setManualForm(f => ({ ...f, captain_user_id: '', captain_name: '' })); manualCaptainAC.reset(); }}>✕</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input
                        type="text"
                        placeholder="Full name *"
                        value={manualNewCaptainName}
                        onChange={e => setManualNewCaptainName(e.target.value)}
                      />
                      <input
                        type="email"
                        placeholder="Email address *"
                        value={manualNewCaptainEmail}
                        onChange={e => setManualNewCaptainEmail(e.target.value)}
                      />
                    </div>
                    <div style={{ marginTop: 5, fontSize: '0.72rem', color: '#8a9bb5' }}>
                      Registration Confirmed and Team Invitation emails will be sent to this address.
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '0.75rem' }}>Enroll in Tournaments</label>
                <TournamentCheckboxes selectedIds={manualTournamentIds} onChange={setManualTournamentIds} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleManualAdd} disabled={!manualForm.team_name.trim() || manualSubmitting}>
                  {manualSubmitting ? 'Adding...' : 'Add Team'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowManualAdd(false); setManualForm({ team_name: '', captain_name: '', captain_user_id: '' }); setManualTournamentIds([]); manualCaptainAC.reset(); setManualCaptainMode('search'); setManualNewCaptainName(''); setManualNewCaptainEmail(''); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {teams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>
          No teams yet. Add teams and assign them to tournaments.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...teams].sort((a, b) => teamSort === 'name'
            ? (a.team_name || '').localeCompare(b.team_name || '')
            : (a.team_number || 0) - (b.team_number || 0)
          ).map(t => {
            const isEditing = editingTeam === t.id;
            const enrolledNames = enrollableTournaments.filter(tourn => (t.tournament_ids || []).includes(tourn.id)).map(tourn => tourn.name);

            return (
              <div key={t.id} className="card" style={{ padding: '16px 20px' }}>
                {isEditing ? (
                  <div>
                    <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 14 }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.75rem' }}>Team Name</label>
                        <input value={editForm.team_name} onChange={e => setEditForm(f => ({ ...f, team_name: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.75rem' }}>Team #</label>
                        <input type="number" min="1" value={editForm.team_number || ''} onChange={e => setEditForm(f => ({ ...f, team_number: e.target.value }))} />
                      </div>
                    </div>
                    {/* Captain selection in edit mode */}
                    <div className="form-group" style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: '0.75rem' }}>Team Captain <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
                      <div style={{ display: 'flex', gap: 0, marginBottom: 8, borderRadius: 6, overflow: 'hidden', border: '1px solid #2a3f56', width: 'fit-content' }}>
                        <button type="button"
                          style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: editCaptainMode === 'search' ? '#4a9eff' : '#1e2d3d', color: editCaptainMode === 'search' ? '#fff' : '#8a9bb5' }}
                          onClick={() => setEditCaptainMode('search')}>Search Existing</button>
                        <button type="button"
                          style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: editCaptainMode === 'new' ? '#e8a838' : '#1e2d3d', color: editCaptainMode === 'new' ? '#0d1b2a' : '#8a9bb5' }}
                          onClick={() => setEditCaptainMode('new')}>Add New Participant</button>
                      </div>
                      {editCaptainMode === 'search' ? (
                        <div style={{ position: 'relative' }}>
                          <input
                            value={editCaptainAC.query}
                            onChange={e => editCaptainAC.handleChange(e.target.value)}
                            onFocus={() => editCaptainAC.query.length >= 1 && editCaptainAC.setShowList(true)}
                            placeholder="Type name, email or phone..."
                            autoComplete="off"
                          />
                          {editCaptainAC.showList && (editCaptainAC.results.length > 0 || editCaptainAC.loading) && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, zIndex: 50, maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                              {editCaptainAC.loading && <div style={{ padding: '10px 14px', color: '#8a9bb5', fontSize: '0.8rem' }}>Searching...</div>}
                              {editCaptainAC.results.map(u => (
                                <button key={u.id} type="button"
                                  onClick={() => { setEditForm(f => ({ ...f, captain_user_id: u.id, captain_name: `${u.first_name} ${u.last_name}` })); editCaptainAC.setQuery(`${u.first_name} ${u.last_name}`); editCaptainAC.setShowList(false); }}
                                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #2a3f56', display: 'flex', justifyContent: 'space-between' }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#2a3f56'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                >
                                  <div>
                                    <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{u.first_name} {u.last_name}</div>
                                    <div style={{ color: '#8a9bb5', fontSize: '0.72rem' }}>{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                                  </div>
                                  <span style={{ color: '#e8a838', fontSize: '0.7rem', alignSelf: 'center' }}>Set Captain</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {editForm.captain_name && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ background: '#e8a838', color: '#0d1b2a', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>CAPTAIN</span>
                              <span style={{ color: '#f0f2f5', fontSize: '0.82rem' }}>{editForm.captain_name}</span>
                              <button type="button" style={{ background: 'none', border: 'none', color: '#5c6d83', cursor: 'pointer', fontSize: '0.8rem' }} onClick={() => { setEditForm(f => ({ ...f, captain_user_id: '', captain_name: '' })); editCaptainAC.reset(); }}>✕</button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <input
                              type="text"
                              placeholder="Full name *"
                              value={editNewCaptainName}
                              onChange={e => setEditNewCaptainName(e.target.value)}
                            />
                            <input
                              type="email"
                              placeholder="Email address *"
                              value={editNewCaptainEmail}
                              onChange={e => setEditNewCaptainEmail(e.target.value)}
                            />
                          </div>
                          <div style={{ marginTop: 5, fontSize: '0.72rem', color: '#8a9bb5' }}>
                            Registration Confirmed and Team Invitation emails will be sent to this address.
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="form-group" style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: '0.75rem' }}>Tournaments Entered</label>
                      <TournamentCheckboxes selectedIds={editForm.tournament_ids || []} onChange={ids => setEditForm(f => ({ ...f, tournament_ids: ids }))} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => handleSaveEdit(t.id)}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditingTeam(null); editCaptainAC.reset(); setEditCaptainMode('search'); setEditNewCaptainName(''); setEditNewCaptainEmail(''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="team-card-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {t.system_number && <span style={{ background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', color: '#4a9eff', fontWeight: 700, fontFamily: 'Oswald, sans-serif' }} title="System-wide team number">SYS#{t.system_number}</span>}
                        {t.team_number && <span style={{ background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 6, padding: '2px 10px', fontSize: '0.8rem', color: '#e8a838', fontWeight: 700, fontFamily: 'Oswald, sans-serif' }}>#{t.team_number}</span>}
                        <span style={{ fontWeight: 600, color: '#f0f2f5' }}>{t.team_name}</span>
                        {(t.captain_name || t.members?.find(m => m.role === 'captain')) && (() => {
                          const captainMember = t.members?.find(m => m.role === 'captain');
                          const captainDisplayName = t.captain_name || (captainMember ? `${captainMember.first_name} ${captainMember.last_name}` : '');
                          const captainId = t.captain_user_id || captainMember?.id;
                          return (
                            <span style={{ color: '#8a9bb5', fontSize: '0.75rem' }}>
                              Capt: {captainId ? (
                                <span
                                  onClick={(e) => { e.stopPropagation(); setHighlightUserId(captainId); setActiveTab('participants'); }}
                                  style={{ color: '#e8a838', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                                >{captainDisplayName}</span>
                              ) : (
                                <span style={{ color: '#e8a838' }}>{captainDisplayName}</span>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {enrolledNames.length === 0 ? (
                          <span style={{ color: '#e85d5d', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconWarning size="12px" />Not enrolled in any tournament</span>
                        ) : enrolledNames.map(name => (
                          <span key={name} style={{ background: '#111f2e', border: '1px solid #2a3f56', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', color: '#8a9bb5' }}>{name}</span>
                        ))}
                      </div>
                    </div>
                    <div className="team-card-actions" style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#4a9eff' }} onClick={() => { setEditingTeam(t.id); setEditForm({ team_name: t.team_name, team_number: t.team_number || '', tournament_ids: t.tournament_ids || [], captain_user_id: t.captain_user_id || '', captain_name: t.captain_name || '' }); editCaptainAC.reset(); setEditCaptainMode('search'); setEditNewCaptainName(''); setEditNewCaptainEmail(''); if (t.captain_name) editCaptainAC.setQuery(t.captain_name); }}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleRemove(t.id, t.team_name)}>Remove</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── SPONSORS TAB ────────────────────────────────────────────
function SponsorsTab({ eventId, event, onRefresh }) {
  const [form, setForm] = useState({ sponsor_name: '', logo_url: '', website_url: '' });
  const [showForm, setShowForm] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploading, setUploading] = useState(false);
  const searchTimeout = useRef(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const sponsors = event.sponsors || [];

  // Bulk add state
  const [showBulkAdd, setShowBulkAdd]             = useState(false);
  const [sponsorLibrary, setSponsorLibrary]       = useState([]);
  const [selectedSponsors, setSelectedSponsors]   = useState(new Set());

  // Editing state
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [editForm, setEditForm]             = useState({ sponsor_name: '', logo_url: '', website_url: '' });
  const [editUploading, setEditUploading]   = useState(false);
  const editFileRef = useRef(null);
  const [loadingLibrary, setLoadingLibrary]       = useState(false);
  const [bulkSubmitting, setBulkSubmitting]       = useState(false);
  const [bulkError, setBulkError]                 = useState('');
  const [bulkSuccess, setBulkSuccess]             = useState('');

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleNameChange(e) {
    const val = e.target.value;
    setForm(f => ({ ...f, sponsor_name: val }));

    clearTimeout(searchTimeout.current);
    if (val.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await tournamentApi.searchSponsors(eventId, val.trim());
        setSearchResults(res.data.data || []);
        setShowDropdown(true);
      } catch { setSearchResults([]); }
    }, 300);
  }

  function handleSelectSponsor(s) {
    setForm({ sponsor_name: s.sponsor_name, logo_url: s.logo_url, website_url: s.website_url || '' });
    setShowDropdown(false);
    setSearchResults([]);
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await tournamentApi.uploadSponsorLogo(eventId, file);
      setForm(f => ({ ...f, logo_url: res.data.logo_url }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    try {
      await tournamentApi.addSponsor(eventId, form);
      setShowForm(false);
      setForm({ sponsor_name: '', logo_url: '', website_url: '' });
      onRefresh();
    } catch (err) {
      alert('Failed to add sponsor');
    }
  }

  function startEditSponsor(s) {
    setEditingSponsor(s.id);
    setEditForm({ sponsor_name: s.sponsor_name, logo_url: s.logo_url, website_url: s.website_url || '' });
  }

  async function handleEditUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setEditUploading(true);
    try {
      const res = await tournamentApi.uploadSponsorLogo(eventId, file);
      setEditForm(f => ({ ...f, logo_url: res.data.logo_url }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setEditUploading(false);
      if (editFileRef.current) editFileRef.current.value = '';
    }
  }

  async function handleSaveEdit(sponsorId) {
    try {
      await tournamentApi.updateSponsor(eventId, sponsorId, editForm);
      setEditingSponsor(null);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update sponsor');
    }
  }

  async function handleRemove(id) {
    if (!window.confirm('Remove this sponsor?')) return;
    try {
      await tournamentApi.removeSponsor(eventId, id);
      onRefresh();
    } catch (err) {
      alert('Failed to remove sponsor');
    }
  }

  // Fetch sponsor library when bulk-add opens
  useEffect(() => {
    if (!showBulkAdd) return;
    setLoadingLibrary(true);
    tournamentApi.getSavedSponsors(eventId)
      .then(res => setSponsorLibrary(res.data.data || []))
      .catch(() => setSponsorLibrary([]))
      .finally(() => setLoadingLibrary(false));
  }, [showBulkAdd, eventId]);

  const existingSponsorNames = new Set(sponsors.map(s => s.sponsor_name.toLowerCase()));
  const availableSponsors = sponsorLibrary.filter(s => !existingSponsorNames.has(s.sponsor_name.toLowerCase()));
  const alreadyAddedSponsors = sponsorLibrary.filter(s => existingSponsorNames.has(s.sponsor_name.toLowerCase()));

  function toggleSponsorSelect(id) {
    setSelectedSponsors(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllSponsors() {
    if (selectedSponsors.size === availableSponsors.length) {
      setSelectedSponsors(new Set());
    } else {
      setSelectedSponsors(new Set(availableSponsors.map(s => s.id)));
    }
  }

  async function handleBulkAddSponsors() {
    if (selectedSponsors.size === 0) return;
    setBulkSubmitting(true);
    setBulkError('');
    setBulkSuccess('');
    try {
      const toAdd = sponsorLibrary.filter(s => selectedSponsors.has(s.id)).map(s => ({ sponsor_name: s.sponsor_name, logo_url: s.logo_url, website_url: s.website_url || '' }));
      const res = await tournamentApi.bulkAddSponsors(eventId, { sponsors: toAdd });
      const { created, skipped } = res.data.data;
      let msg = `${created.length} sponsor(s) added.`;
      if (skipped.length > 0) msg += ` ${skipped.length} skipped (already added).`;
      setBulkSuccess(msg);
      setSelectedSponsors(new Set());
      onRefresh();
    } catch (err) {
      setBulkError(err.response?.data?.message || 'Failed to add sponsors');
    } finally {
      setBulkSubmitting(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowBulkAdd(!showBulkAdd); setBulkError(''); setBulkSuccess(''); if (showForm) setShowForm(false); }}>
          {showBulkAdd ? 'Cancel' : '+ Add Sponsors'}
        </button>
      </div>

      {/* Bulk add sponsors panel */}
      {showBulkAdd && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 4 }}>Add Sponsors from Library</h4>
          <p style={{ color: '#8a9bb5', fontSize: '0.8rem', marginBottom: 16 }}>Select sponsors from your saved library to add to this event.</p>

          {bulkError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{bulkError}</div>}
          {bulkSuccess && <div className="alert alert-success" style={{ marginBottom: 12, background: 'rgba(46,204,113,0.1)', border: '1px solid #2ecc71', color: '#2ecc71', padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem' }}>{bulkSuccess}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#8a9bb5', fontSize: '0.82rem' }}>
              <input
                type="checkbox"
                checked={availableSponsors.length > 0 && selectedSponsors.size === availableSponsors.length}
                onChange={selectAllSponsors}
                style={{ accentColor: '#4a9eff', width: 16, height: 16 }}
              />
              Select all available ({availableSponsors.length})
            </label>
            {selectedSponsors.size > 0 && (
              <span style={{ color: '#4a9eff', fontSize: '0.82rem', fontWeight: 600 }}>{selectedSponsors.size} selected</span>
            )}
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #2a3f56', borderRadius: 8, marginBottom: 16 }}>
            {loadingLibrary ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#8a9bb5' }}>Loading sponsor library...</div>
            ) : sponsorLibrary.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#8a9bb5' }}>No sponsors in library. Add sponsors individually below.</div>
            ) : (
              <>
                {availableSponsors.map(s => {
                  const isSelected = selectedSponsors.has(s.id);
                  return (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1e2d3d', background: isSelected ? 'rgba(74,158,255,0.06)' : 'transparent' }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#1a2a3a'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? 'rgba(74,158,255,0.06)' : 'transparent'; }}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSponsorSelect(s.id)} style={{ accentColor: '#4a9eff', width: 16, height: 16, flexShrink: 0 }} />
                      <img src={assetUrl(s.logo_url)} alt="" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, background: '#fff', padding: 1, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                      <span style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>{s.sponsor_name}</span>
                    </label>
                  );
                })}
                {alreadyAddedSponsors.length > 0 && (
                  <>
                    <div style={{ padding: '8px 14px', background: '#111f2e', color: '#5c6d83', fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid #1e2d3d' }}>Already in this event</div>
                    {alreadyAddedSponsors.map(s => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #1e2d3d', opacity: 0.5 }}>
                        <input type="checkbox" disabled checked style={{ width: 16, height: 16, flexShrink: 0 }} />
                        <img src={assetUrl(s.logo_url)} alt="" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, background: '#fff', padding: 1, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                        <span style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>{s.sponsor_name}</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={handleBulkAddSponsors} disabled={selectedSponsors.size === 0 || bulkSubmitting}>
              {bulkSubmitting ? 'Adding...' : `Add ${selectedSponsors.size} Sponsor${selectedSponsors.size !== 1 ? 's' : ''}`}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowBulkAdd(false); setSelectedSponsors(new Set()); }}>Cancel</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" style={{ color: '#e8a838' }} onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Hide' : '+ Add Sponsor'}
            </button>
          </div>

          {/* Manual add single sponsor form */}
          {showForm && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid #e8a838', borderRadius: 8, background: 'rgba(232,168,56,0.04)' }}>
              <h5 style={{ margin: '0 0 12px', color: '#e8a838', fontSize: '0.85rem' }}>Add New Sponsor</h5>
              <form onSubmit={handleAdd}>
                <div className="sponsor-add-row" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0, position: 'relative' }} ref={dropdownRef}>
                    <label style={{ fontSize: '0.75rem' }}>Sponsor Name</label>
                    <input
                      value={form.sponsor_name}
                      onChange={handleNameChange}
                      onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                      placeholder="Search or type new name..."
                      required
                      autoComplete="off"
                    />
                    {showDropdown && searchResults.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                        background: '#0d1a26', border: '1px solid #2a3f56', borderRadius: 8,
                        maxHeight: 240, overflowY: 'auto', marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                      }}>
                        {searchResults.map(s => (
                          <div
                            key={s.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                              cursor: 'pointer', borderBottom: '1px solid #1e2d3d', transition: 'background 0.15s'
                            }}
                            onClick={() => handleSelectSponsor(s)}
                            onMouseOver={e => e.currentTarget.style.background = '#111f2e'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <img
                              src={assetUrl(s.logo_url)}
                              alt=""
                              style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, background: '#fff', flexShrink: 0 }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ color: '#f0f2f5', fontSize: '0.88rem' }}>{s.sponsor_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group" style={{ flex: '2 1 300px', marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Logo</label>
                    <div className="sponsor-logo-input-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        value={form.logo_url}
                        onChange={e => setForm({ ...form, logo_url: e.target.value })}
                        placeholder="https://... or upload a file"
                        required
                        style={{ flex: 1 }}
                      />
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".png,.jpg,.jpeg,.svg,.webp"
                        onChange={handleUpload}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        {uploading ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                    {form.logo_url && (
                      <div style={{ marginTop: 8 }}>
                        <img
                          src={assetUrl(form.logo_url)}
                          alt="Preview"
                          style={{ height: 40, maxWidth: 120, objectFit: 'contain', borderRadius: 4, background: '#fff', padding: 2 }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group" style={{ flex: '2 1 300px', marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Website URL</label>
                    <input
                      value={form.website_url}
                      onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))}
                      placeholder="https://sponsor-website.com"
                      type="url"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end', marginBottom: 2 }}>Add</button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
      {sponsors.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>No sponsors added yet. Add sponsor logos to display on the scoreboard.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sponsors.map(s => (
            <div key={s.id} className="card" style={{ padding: '16px 20px' }}>
              {editingSponsor === s.id ? (
                <div>
                  <div className="grid-responsive" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Sponsor Name</label>
                      <input value={editForm.sponsor_name} onChange={e => setEditForm(f => ({ ...f, sponsor_name: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Website URL</label>
                      <input value={editForm.website_url} onChange={e => setEditForm(f => ({ ...f, website_url: e.target.value }))} placeholder="https://..." />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: '0.75rem' }}>Logo</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input value={editForm.logo_url} onChange={e => setEditForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="Logo URL" style={{ flex: 1 }} />
                      <input type="file" ref={editFileRef} accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleEditUpload} style={{ display: 'none' }} />
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => editFileRef.current?.click()} disabled={editUploading} style={{ whiteSpace: 'nowrap' }}>
                        {editUploading ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                    {editForm.logo_url && (
                      <div style={{ marginTop: 8 }}>
                        <img src={assetUrl(editForm.logo_url)} alt="Preview" style={{ height: 40, maxWidth: 120, objectFit: 'contain', borderRadius: 4, background: '#fff', padding: 2 }} onError={e => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handleSaveEdit(s.id)}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingSponsor(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                    <img
                      src={assetUrl(s.logo_url)}
                      alt={s.sponsor_name}
                      style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, background: '#fff', padding: 2, flexShrink: 0 }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <div>
                      <span style={{ fontWeight: 600, color: '#f0f2f5', fontSize: '0.88rem' }}>{s.sponsor_name}</span>
                      {s.website_url && <div style={{ color: '#4a9eff', fontSize: '0.72rem', marginTop: 2 }}>{s.website_url}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" style={{ color: '#4a9eff' }} onClick={() => startEditSponsor(s)}>Edit</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleRemove(s.id)}>Remove</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TOURNAMENT VISIBILITY TOGGLES ──────────────────────────
function TournamentVisibilityToggles({ eventId, tournaments, onRefresh }) {
  const [toggling, setToggling] = useState(null);

  // Only show parent tournaments (no day variants)
  const parentTournaments = tournaments.filter(t => !t.parent_tournament_id);

  if (parentTournaments.length === 0) return null;

  async function handleToggle(tournId, currentVisible) {
    setToggling(tournId);
    try {
      await tournamentApi.toggleScoreboardVisible(eventId, tournId, !currentVisible);
      onRefresh();
    } catch (err) {
      console.error('Toggle visibility error:', err);
    } finally {
      setToggling(null);
    }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ color: '#8a9bb5', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Tournament Boards</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {parentTournaments.map(t => (
          <div
            key={t.id}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', background: '#111f2e', borderRadius: 10,
              border: '1px solid #1e2d3d', opacity: toggling === t.id ? 0.6 : 1,
              transition: 'opacity 0.2s'
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.name.replace(/\s*\(Day\s*\d+\)\s*$/, '')}
              </div>
              <div style={{ color: '#5c6d83', fontSize: '0.72rem', marginTop: 2 }}>
                {t.tournament_type === 'calcutta' ? 'Calcutta' : t.is_main_tournament ? 'Main tournament' : 'Secondary'}
                {t.num_days > 1 ? ` · ${t.num_days} days` : ''}
              </div>
            </div>
            <div
              onClick={() => !toggling && handleToggle(t.id, t.scoreboard_visible !== false)}
              style={{
                width: 44, height: 24, borderRadius: 12, flexShrink: 0,
                background: (t.scoreboard_visible !== false) ? '#3adc8a' : '#2a3f56',
                position: 'relative', transition: 'background 0.2s', cursor: toggling ? 'wait' : 'pointer'
              }}
            >
              <div style={{
                position: 'absolute', top: 3, left: (t.scoreboard_visible !== false) ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ color: '#5c6d83', fontSize: '0.7rem', marginTop: 8, fontStyle: 'italic' }}>
        Toggle which tournament boards appear on the live scoreboard.
      </div>
    </div>
  );
}

// ─── SCOREBOARD TAB ──────────────────────────────────────────
function ScoreboardTab({ eventId, event, tournaments, onRefresh }) {
  const [form, setForm] = useState({
    branding_primary_color:    event.branding_primary_color    || '#1a3c5e',
    branding_secondary_color:  event.branding_secondary_color  || '#e8a838',
    scoreboard_rotation_seconds: event.scoreboard_rotation_seconds || 10,
    scoreboard_table_header_bg:    event.scoreboard_table_header_bg    || '#e8a838',
    scoreboard_table_header_color: event.scoreboard_table_header_color || '#0d1b2a',
    scoreboard_table_row_color:    event.scoreboard_table_row_color    || '#0d1b2a',
    scoreboard_table_row_border:   event.scoreboard_table_row_border   || '#eeeeee',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      await eventsApi.updateEvent(eventId, form);
      setSaved(true);
      onRefresh();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  const Toggle = ({ label, sub, field }) => (
    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: '#111f2e', borderRadius: 10, border: '1px solid #1e2d3d', cursor: 'pointer', gap: 12 }}>
      <div>
        <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.88rem' }}>{label}</div>
        {sub && <div style={{ color: '#5c6d83', fontSize: '0.75rem', marginTop: 2 }}>{sub}</div>}
      </div>
      <div
        onClick={() => setForm(f => ({ ...f, [field]: !f[field] }))}
        style={{
          width: 44, height: 24, borderRadius: 12, flexShrink: 0,
          background: form[field] ? '#3adc8a' : '#2a3f56',
          position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: form[field] ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
        }} />
      </div>
    </label>
  );

  const scorboardUrl = `${window.location.origin}/events/${eventId}/scoreboard`;

  return (
    <form onSubmit={handleSave}>
      <div className="scoreboard-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

        {/* ── Column 1: Display options ── */}
        <div>
          <div style={{ color: '#8a9bb5', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Display Options</div>

          <div style={{ background: '#f8f9fb', border: '1px solid #e0e3e8', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#555', marginBottom: 20 }}>
            <strong style={{ color: '#0d1b2a' }}>Scoreboard column visibility</strong> is now configured per tournament.
            Open any tournament's settings and look for "Live Scoreboard Outputs" at the bottom of the form.
          </div>

          <div className="card" style={{ padding: '16px 18px', marginBottom: 0 }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Rotation Speed</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <input
                type="range" min="5" max="60" step="5"
                value={form.scoreboard_rotation_seconds}
                onChange={e => setForm(f => ({ ...f, scoreboard_rotation_seconds: parseInt(e.target.value) }))}
                style={{ flex: 1 }}
              />
              <div style={{ minWidth: 60, textAlign: 'right', fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '1.2rem', color: '#e8a838' }}>
                {form.scoreboard_rotation_seconds}s
              </div>
            </div>
            <div style={{ color: '#5c6d83', fontSize: '0.72rem', marginTop: 6 }}>
              Time each tournament board is shown before rotating
            </div>
          </div>

          {/* Tournament Display Toggles */}
          <TournamentVisibilityToggles eventId={eventId} tournaments={tournaments} onRefresh={onRefresh} />
        </div>

        {/* ── Column 2: Colors + Preview + Link ── */}
        <div>
          <div style={{ color: '#8a9bb5', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Table Colours</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Table header background', field: 'scoreboard_table_header_bg', placeholder: '#e8a838' },
              { label: 'Table header font colour', field: 'scoreboard_table_header_color', placeholder: '#0d1b2a' },
              { label: 'Row font colour', field: 'scoreboard_table_row_color', placeholder: '#0d1b2a' },
              { label: 'Row divider colour', field: 'scoreboard_table_row_border', placeholder: '#eeeeee' },
            ].map(({ label, field, placeholder }) => (
              <div key={field} className="form-group" style={{ marginBottom: 0 }}>
                <label>{label}</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    style={{ width: 48, height: 38, padding: 2, borderRadius: 6, border: '1px solid #2a3f56', background: 'transparent', cursor: 'pointer' }}
                  />
                  <input
                    value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={placeholder}
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.82rem' }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div style={{ color: '#5c6d83', fontSize: '0.7rem', marginBottom: 20, fontStyle: 'italic' }}>
            Score and Big Fish columns always use brand gold and blue respectively and cannot be adjusted.
          </div>

          {/* Live preview swatch */}
          <div style={{
            borderRadius: 10, overflow: 'hidden', marginBottom: 20,
            background: '#ffffff', padding: '18px 20px', border: '1px solid #2a3f56'
          }}>
            <div style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#0d1b2a', marginBottom: 8 }}>{event.name || 'Event Name'}</div>
            <div style={{ display: 'flex', gap: 0 }}>
              {['POS', 'TEAM', 'SCORE'].map((col, i) => (
                <div key={col} style={{
                  flex: 1, padding: '6px 8px', textAlign: 'center',
                  background: form.scoreboard_table_header_bg, color: form.scoreboard_table_header_color,
                  fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: '0.7rem', letterSpacing: 1,
                  ...(i === 0 ? { borderRadius: '6px 0 0 6px' } : i === 2 ? { borderRadius: '0 6px 6px 0' } : {})
                }}>{col}</div>
              ))}
            </div>
            {[1, 2, 3].map(n => (
              <div key={n} style={{ display: 'flex', gap: 0, borderBottom: n < 3 ? `1px solid ${form.scoreboard_table_row_border}` : 'none' }}>
                <div style={{ flex: 1, padding: '6px 8px', textAlign: 'center', fontFamily: 'Oswald, sans-serif', fontSize: '0.85rem' }}>
                  {n === 1 ? '1st' : n === 2 ? '2nd' : '3rd'}
                </div>
                <div style={{ flex: 1, padding: '6px 8px', textAlign: 'center', fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: '0.8rem', color: form.scoreboard_table_row_color }}>
                  Team {n}
                </div>
                <div style={{ flex: 1, padding: '6px 8px', textAlign: 'center', fontFamily: 'Oswald, sans-serif', fontWeight: 800, fontSize: '0.85rem', color: '#e8a838' }}>
                  —.—
                </div>
              </div>
            ))}
          </div>

          {/* Scoreboard URL */}
          <div style={{ background: '#111f2e', border: '1px solid #1e2d3d', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}><IconMonitor size="13px" />Scoreboard URL</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, color: '#4a9eff', fontSize: '0.75rem', fontFamily: 'monospace', wordBreak: 'break-all', background: '#0d1b2a', padding: '8px 10px', borderRadius: 6 }}>
                {scorboardUrl}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  if (navigator.clipboard) { navigator.clipboard.writeText(scorboardUrl); }
                  else { const el = document.createElement('textarea'); el.value = scorboardUrl; el.style.position = 'fixed'; el.style.opacity = '0'; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
                }}
                style={{ flexShrink: 0, fontSize: '0.75rem' }}
              >
                Copy
              </button>
              <a
                href={scorboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ flexShrink: 0, fontSize: '0.75rem', textDecoration: 'none' }}
              >
                Open ↗
              </a>
            </div>
            <div style={{ color: '#5c6d83', fontSize: '0.7rem', marginTop: 8 }}>
              Cast this URL to a TV or second screen. No login required.
            </div>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Scoreboard Settings'}
        </button>
        {saved  && <span style={{ color: '#3adc8a', fontSize: '0.85rem', fontWeight: 600 }}>✓ Saved</span>}
        {error  && <span style={{ color: '#e85d5d', fontSize: '0.85rem' }}>{error}</span>}
      </div>
    </form>
  );
}
